// ============================================================================
// parser.js — the cue DSL: tokens, grammar (Chevrotain 11) and CST -> AST
// ============================================================================
//
// parseCueToAST(text) is the entry point: the lexer (allTokens), one shared
// CueParser (sharedParser) and cstToAst, which builds each cue's AST. Values
// go through one extractor, extractVal(); parameter lists through
// extractGenericArgs() (an args list) or extractGenericParamsFlat() (a
// params object, for the audio family, notation and pause).
//
// ============================================================================
const OSCILLA_DSL_DEBUG = false;

import {
  createToken,
  Lexer,
  CstParser,
} from "../vendor/chevrotain.mjs";

import { maybeConvertLink, maybeConvertToCondition } from './parserSignalRef.js';
import { PATTERN_NAMES, PATTERN_WORDS, patternName } from './cueNames.js';

// ─────────────────────────────────────────────────────────────
//  Helper: debug printer
// ─────────────────────────────────────────────────────────────
function printCST(node, depth = 0) {
  if (!node) return;
  const pad = " ".repeat(depth * 2);
  if (node.image !== undefined && node.tokenType) {
    console.log(`${pad}- TOKEN ${node.tokenType.name}: "${node.image}"`);
    return;
  }
  if (node.name) console.log(`${pad}${node.name}`);
  if (node.children) {
    for (const [k, v] of Object.entries(node.children)) {
      console.log(`${pad}  [${k}]:`);
      if (Array.isArray(v)) v.forEach((c) => printCST(c, depth + 2));
      else printCST(v, depth + 2);
    }
  }
}

// ============================================================================
// 1️⃣ TOKEN DEFINITIONS (unchanged from v2)
// ============================================================================

// Hex colors like #fff, #aabbcc, #aabbccdd (must be before NumberLiteral)
const HexColor = createToken({
  name: "HexColor",
  pattern: /#[0-9a-fA-F]{3,8}/,
});

const NumberLiteral = createToken({
  name: "NumberLiteral",
  pattern: /[-+]?[0-9]+(?:\.[0-9]+)?/,
});

const StringLiteral = createToken({
  name: "StringLiteral",
  pattern: /"[^"]*"|'[^']*'/,
});

// Note names like ~A4, ~C#3, ~Bb2, ~Db5 - prefixed with tilde
// Optional cents offset: ~A4+50 (quarter sharp), ~C#3-25 (slightly flat),
// ~A4+12.5, or drawn: ~G2+rand(0, 25), ~G2+birand(5, 20) (synth.js pitchToHz
// rolls it each time the note is played: a pattern's step, a run)
const NUM = String.raw`-?\d+(?:\.\d+)?`;
const CENTS_RAND = String.raw`(?:rand|irand|birand)\(\s*${NUM}\s*,\s*${NUM}\s*\)`;
const CENTS = String.raw`[+-](?:${CENTS_RAND}|${NUM})`;
const NoteLiteral = createToken({
  name: "NoteLiteral",
  // Tilde-prefixed (~A4, ~C#3, ~Bb2, cents: ~A4+50), bare-with-sharp (C#3),
  // or bare with cents up (E1+10). Bare naturals/flats (A4, Bb2) lex as
  // Identifier and resolve in pitchToHz, so the tilde is optional; a token is
  // needed only where a character cannot be in an Identifier: '#' and '+'.
  // Cents down keep the tilde: E1-30 is a name as well (ids have hyphens).
  pattern: new RegExp(String.raw`~[A-Ga-g][#b]?-?\d+(?:${CENTS})?|[A-Ga-g]#-?\d+(?:${CENTS})?|[A-Ga-g]b?-?\d+\+(?:${CENTS_RAND}|\d+(?:\.\d+)?)`),
});

const Identifier = createToken({
  name: "Identifier",
  pattern: /[a-zA-Z_][a-zA-Z0-9_.-]*/
});

const WS = createToken({
  name: "WS",
  pattern: /\s+/,
  group: Lexer.SKIPPED,
});

// Keywords must precede Identifier. A keyword matched only up to \b needs
// longer_alt: Identifier, or a name that goes on with - or . (synth-1,
// audio.txt, button-1) lexes as the keyword and the rest.
const Cue = createToken({ name: "Cue", pattern: /\bcue\b/, longer_alt: Identifier });
const Fade = createToken({ name: "Fade", pattern: /fade\b/, longer_alt: Identifier });
const Page = createToken({ name: "Page", pattern: /\bpage\b/, longer_alt: Identifier });
const Stopwatch = createToken({ name: "Stopwatch", pattern: /\bstopwatch\b/, longer_alt: Identifier });
// adc: audio input, named for Pd/Max's adc~ rather than "mic", so the word
// means the same thing here as it does in the tools people arrive from.
// Matches only where adc is a CUE CALL — the lookahead for "(" is load
// bearing. Without it the lexer claims the word everywhere, and
// follow(adc.amp, …) stops parsing: the source of the signal would become a
// keyword. Every cue-name token has this hazard; here it is guaranteed to
// bite, because binding to adc.* is the entire point of the cue.
const Adc = createToken({ name: "Adc", pattern: /\badc(?=\s*\()/ });
// draw(): the ink an animated object leaves behind. Same lookahead as adc —
// "draw" is an ordinary word that will turn up in element ids and uids, and a
// bare keyword token would claim every one of them.
const Draw = createToken({ name: "Draw", pattern: /\bdraw(?=\s*\()/ });
const Zoom = createToken({ name: "Zoom", pattern: /\bzoom(?=\s*\()/ });
// hush(): silence sound -- all of it, or the voices named by uid:
const Hush = createToken({ name: "Hush", pattern: /\bhush(?=\s*\()/ });
// tempo(120): the beat grid's speed (beatGrid.js) -- only before "(", so
// tempo stays an ordinary word everywhere else
const Tempo = createToken({ name: "Tempo", pattern: /\btempo(?=\s*\()/ });
// shape(): make a polygon in the score (cues/shape.js); "shape" is an
// ordinary word, so only shape( is the cue.
const Shape = createToken({ name: "Shape", pattern: /\bshape(?=\s*\()/ });
// viz(): the look of what a uid draws -- a sound's box, a made shape
const Viz = createToken({ name: "Viz", pattern: /\bviz(?=\s*\()/ });
// station() / leg(): map mode — a place on the route and a move between two
// (route.js). Lookahead for the same reason as draw: both are ordinary words.
const Station = createToken({ name: "Station", pattern: /\bstation(?=\s*\()/ });
const Leg = createToken({ name: "Leg", pattern: /\bleg(?=\s*\()/ });
// random(), not rand(): rand(min,max) is already a value function inside
// patterns and value lists ("values:Pseq(1, rand(0.5, 2), inf)"), and unlike
// adc there is no lookahead that separates them — both are literally
// "rand(". A keyword token would break every score using it.
const Random = createToken({ name: "Random", pattern: /\brandom(?=\s*\()/ });
// use() is a PREPROCESSOR directive, not a cue — it is deliberately absent
// from CUE_PREFIX_RE and the dispatcher. It is in the grammar only so the
// reuse preprocessor can read its parameters with the real parser instead
// of a hand-rolled regex, which could not cope with nested calls like
// use(path:blocks, index:Prand(0,1,2)).
// \b before "use" cannot match inside "reuse(" — both sides are word chars.
const Use = createToken({ name: "Use", pattern: /\buse(?=\s*\()/ });
const Video = createToken({ name: "Video", pattern: /\bvideo\b/, longer_alt: Identifier });
const Text = createToken({ name: "Text", pattern: /\btext\b/, longer_alt: Identifier });
const Pause = createToken({ name: "Pause", pattern: /\bpause\b/, longer_alt: Identifier });
const Stop = createToken({ name: "Stop", pattern: /\bstop\b/, longer_alt: Identifier });
const Pin = createToken({ name: "Pin", pattern: /\bpin\b/, longer_alt: Identifier });
const Nav = createToken({ name: "Nav", pattern: /nav\b/, longer_alt: Identifier });
const Audio = createToken({ name: "Audio", pattern: /audio\b/, longer_alt: Identifier });
const AudioPool = createToken({ name: "AudioPool", pattern: /audioPool\b/, longer_alt: Identifier });
const AudioImpulse = createToken({ name: "AudioImpulse", pattern: /audioImpulse\b/, longer_alt: Identifier });
const Synth = createToken({ name: "Synth", pattern: /synth\b/, longer_alt: Identifier });
const Button = createToken({ name: "Button", pattern: /button\b/, longer_alt: Identifier });
const Rotate = createToken({ name: "Rotate", pattern: /\brotate\b/, longer_alt: Identifier });
const Trans = createToken({ name: "Trans", pattern: /\btrans\b/, longer_alt: Identifier });
const Scale = createToken({ name: "Scale", pattern: /\bscale\b/, longer_alt: Identifier });
const ScaleXY = createToken({ name: "ScaleXY", pattern: /\bscaleXY\b/, longer_alt: Identifier });
const Osc = createToken({ name: "Osc", pattern: /\bosc\b/, longer_alt: Identifier });
const Midi = createToken({ name: "Midi", pattern: /\bmidi\b/, longer_alt: Identifier });
const OscCtrl = createToken({ name: "OscCtrl", pattern: /\boscCtrl\b/, longer_alt: Identifier });
const ControlXY = createToken({ name: "ControlXY", pattern: /\bcontrolXY\b/, longer_alt: Identifier });
const O2P = createToken({ name: "O2P", pattern: /\bo2p\b/, longer_alt: Identifier });
const Color = createToken({
  name: "Color",
  pattern: /\b(color|colour)(?=\s*\()/,
  longer_alt: Identifier
});
const Ui = createToken({ name: "Ui", pattern: /\bui\b/, longer_alt: Identifier });
const Ext = createToken({ name: "Ext", pattern: /\bext\b/, longer_alt: Identifier });
const Notation = createToken({ name: "Notation", pattern: /\bnotation\b/, longer_alt: Identifier });
const After = createToken({ name: "After", pattern: /after\b/, longer_alt: Identifier });

// OSC address paths like /polygon/chfx/ch0
// OSC addresses and slash paths, bare: /oscilla/rotate/abc  rotate/abc
// /synth/note/{i}  sfx/rain. Needs a leading slash or an internal one, so
// plain words stay Identifiers. First in allTokens: the keyword tokens would
// otherwise eat "rotate" out of rotate/abc.
const OscAddr = createToken({
  name: "OscAddr",
  pattern: /\/?[a-zA-Z0-9_][a-zA-Z0-9_.{}\-]*(?:\/[a-zA-Z0-9_.{}\-]+)+|\/[a-zA-Z0-9_][a-zA-Z0-9_.{}\-]*/
});

// A value relative to what it is now: rate:*0.9 (times), pan:+=0.2 / pan:-=0.2
// (by) -- cues/relative.js. Never a number on its own: *, += and -= start no number.
const RelLiteral = createToken({
  name: "RelLiteral",
  pattern: /(?:\*=?|[+\-]=)\s*[-+]?[0-9]+(?:\.[0-9]+)?/,
});

const RangeLiteral = createToken({
  name: "RangeLiteral",
  pattern: /[0-9]+(?:\.[0-9]+)?-[0-9]+(?:\.[0-9]+)?/
});

// Punctuation
const LParen = createToken({ name: "LParen", pattern: /\(/ });
const RParen = createToken({ name: "RParen", pattern: /\)/ });
const LBrace = createToken({ name: "LBrace", pattern: /\{/ });
const RBrace = createToken({ name: "RBrace", pattern: /\}/ });
const LBracket = createToken({ name: "LBracket", pattern: /\[/ });
const RBracket = createToken({ name: "RBracket", pattern: /\]/ });
const Colon = createToken({ name: "Colon", pattern: /:/ });
const Comma = createToken({ name: "Comma", pattern: /,/ });
// seek(50%) -- a share of the score. Nowhere else in the language.
const Percent = createToken({ name: "Percent", pattern: /%/ });
const At = createToken({ name: "At", pattern: /@/ });
// midi:cc64@1 -- a MIDI message on one channel (midiSpec.js), one token: as
// a word, an @ and a number it was cc64, @, 1
const MidiSpec = createToken({ name: "MidiSpec", pattern: /\b(?:note|cc|pc)\d{1,3}@\d{1,2}\b/ });
// freq:Pwhite(40, 100) | scale(minor): a value through pitch filters (pitchSieve.js)
const Pipe = createToken({ name: "Pipe", pattern: /\|/ });
// sieve(3@0 + 4@1 * 2@0): Xenakis residue classes, one token -- before the
// numbers, which would else take the 3 and leave @0 dangling
const SieveLiteral = createToken({ name: "SieveLiteral", pattern: /\d+\s*@\s*-?\d+(?:\s*[+*]\s*\d+\s*@\s*-?\d+)*/ });
// "x": the key x: (scale(x:...)) or the x of a size pair (45x24) -- only a
// lone x, or one between digits. A name that starts with x (x1, xylo,
// x.png, x-1) is an ordinary Identifier: matched as /x/ it was split into x
// and the rest.
const XParam = createToken({
  name: "XParam",
  pattern: {
    exec: (text, i) => {
      if (text[i] !== "x") return null;
      const prev = text[i - 1] ?? "", next = text[i + 1] ?? "";
      const lone = !/[A-Za-z0-9_]/.test(prev) && !/[A-Za-z0-9_.-]/.test(next);
      const pair = /[0-9]/.test(prev) && /[0-9]/.test(next);
      return lone || pair ? ["x"] : null;
    },
  },
  start_chars_hint: ["x"],
  line_breaks: false,
});
// the tokens an animation cue's key: can be
const ANIM_KEYS = [Identifier, Rotate, Osc, Pin, XParam, Midi];
const True = createToken({ name: "True", pattern: /\btrue\b/ });
const False = createToken({ name: "False", pattern: /\bfalse\b/ });

/** The pattern calls, Pseq(...) to Pexprand(...) (cueNames.js); propagate() passes them through. */
export { PATTERN_NAMES, PATTERN_WORDS };
// pseq(...) as the docs write it, Pseq(...) as it was: the lowercase only
// before "(", so a uid or a page called pseq is still a name
const PatternName = createToken({
  name: "PatternName",
  pattern: new RegExp(`(?:${PATTERN_NAMES.join("|")})\\b|(?:${PATTERN_WORDS.join("|")})\\b(?=\\s*\\()`),
  start_chars_hint: ["P", "p"],
});

/** Cue names matched as plain identifiers, behind a gate (cueTransportTop). */
const TRANSPORT_WORDS = new Set(["play", "rewind", "seek"]);

const allTokens = [
  OscAddr,   // slash paths — before the keywords (see the token comment)
  MidiSpec,  // cc64@1 — before the words and the @
  Cue, Fade, Page, Stopwatch, Adc, Random, Use, Video, Text, Pause, Stop, Pin,
  Audio, AudioPool, AudioImpulse, Synth, Button, Nav,
  Rotate, Trans, Scale, ScaleXY, O2P, Color, Ui, Ext, Notation, Draw, Zoom, Hush, Tempo, Shape, Viz, Station, Leg,
  Osc, Midi, OscCtrl, ControlXY,
  After, PatternName,
  LParen, RParen, LBrace, RBrace, LBracket, RBracket, Colon, Comma, Percent, Pipe, SieveLiteral, At, XParam,
  HexColor,  // Must be before NumberLiteral to capture #aabbcc colors
  RelLiteral, RangeLiteral, NumberLiteral, StringLiteral, True, False,
  NoteLiteral,  // Note names (A4, C#3, Bb2) - must precede Identifier
  Identifier, WS
];

export const CueLexer = new Lexer(allTokens);

// ============================================================================
// 2️⃣ PARSER (grammar rules — same structure, minor cleanup)
// ============================================================================
class CueParser extends CstParser {
  constructor() {
    super(allTokens);
    const $ = this;

    $.RULE("animValue", () => {
      return $.OR([
        { ALT: () => $.SUBRULE($.arrayValue) },
        { GATE: () => $.LA(1).tokenType === PatternName, ALT: () => $.SUBRULE($.patternCall) },
        { ALT: () => $.CONSUME(HexColor) },
        { ALT: () => $.CONSUME(OscAddr) },
        { ALT: () => $.CONSUME(NumberLiteral) },
        { ALT: () => $.CONSUME(StringLiteral) },
        { ALT: () => $.CONSUME(NoteLiteral) },
        { ALT: () => $.CONSUME(True) },
        { ALT: () => $.CONSUME(False) },
        {
          GATE: () => ($.LA(1).tokenType.name === "Identifier" || $.LA(1).tokenType.name === "Midi") && $.LA(2).tokenType.name === "LParen",
          ALT: () => $.SUBRULE($.simpleFuncCall)
        },
        { ALT: () => $.CONSUME(Identifier) },
        // a lone x is the size token (45x24), but as a value it is a word:
        // rotate(axis:x)
        { ALT: () => $.CONSUME(XParam) },
        // "midi" is the midi() cue's token, and a word as a value: trig:midi
        { ALT: () => $.CONSUME(Midi) },
        { ALT: () => $.CONSUME(MidiSpec) },                       // midi:cc64@1
      ]);
    });

    $.RULE("arrayValue", () => {
      $.CONSUME(LBracket);
      const items = [];
      $.MANY_SEP({ SEP: Comma, DEF: () => items.push($.SUBRULE($.animValue)) });
      $.CONSUME(RBracket);
      return items;
    });

    $.RULE("animGenericParam", () => {
      const keyTok = $.OR([
        { ALT: () => $.CONSUME(Identifier, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Rotate, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Osc, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Pin, { LABEL: "key" }) },
        // "x" lexes as the size-pair token (45x24): scale(x:..., y:...)
        { ALT: () => $.CONSUME(XParam, { LABEL: "key" }) },
        // rotate(trig:midi, midi:note60) -- "midi" is the midi() cue's token
        { ALT: () => $.CONSUME(Midi, { LABEL: "key" }) },
      ]);
      $.CONSUME(Colon);
      $.SUBRULE($.animValue, { LABEL: "value" });
      return { key: keyTok.image };
    });

    $.RULE("animGenericParamList", () => {
      $.CONSUME(LParen);
      $.OR([
        {
          GATE: () => !(ANIM_KEYS.includes($.LA(1).tokenType) && $.LA(2).tokenType === Colon),
          ALT: () => {
            $.SUBRULE($.animValue, { LABEL: "firstValue" });
            // scaleXY([1, 1.3], [1, 0.6], ...): a second value before the keys
            $.OPTION2({
              GATE: () => $.LA(1).tokenType === Comma && !(ANIM_KEYS.includes($.LA(2).tokenType) && $.LA(3).tokenType === Colon),
              DEF: () => { $.CONSUME2(Comma); $.SUBRULE2($.animValue, { LABEL: "secondValue" }); },
            });
            $.OPTION(() => $.CONSUME(Comma));
            $.MANY_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.animGenericParam, { LABEL: "restParams" }) });
          }
        },
        {
          ALT: () => {
            $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE2($.animGenericParam, { LABEL: "kvParams" }) });
          }
        }
      ]);
      $.CONSUME(RParen);
    });

    // ---- Object literal support ----
    $.RULE("objectValue", () => {
      $.OR([
        { GATE: () => $.LA(1).tokenType === PatternName, ALT: () => {
          $.SUBRULE($.patternCall);
          $.MANY(() => $.CONSUME2(RelLiteral, { LABEL: "patOp" }));        // filter:{freq:Pseq(400, 800, inf)*2}
        } },
        { GATE: () => ($.LA(1).tokenType === Identifier || $.LA(1).tokenType === Midi) && $.LA(2).tokenType === LParen, ALT: () => $.SUBRULE($.simpleFuncCall) },
        { GATE: () => $.LA(1).tokenType === LBracket, ALT: () => $.SUBRULE($.arrayValue) },
        { ALT: () => $.CONSUME(RelLiteral) },           // reverb:{mix:*1.5}
        { ALT: () => $.CONSUME(NumberLiteral) },
        { ALT: () => $.CONSUME(StringLiteral) },
        { ALT: () => $.CONSUME(NoteLiteral) },
        { ALT: () => $.CONSUME(True) },
        { ALT: () => $.CONSUME(False) },
        { ALT: () => $.CONSUME(Identifier) },
      ]);
    });

    $.RULE("objectPair", () => {
      $.CONSUME(Identifier, { LABEL: "key" });
      $.CONSUME(Colon);
      $.SUBRULE($.objectValue, { LABEL: "value" });
    });

    $.RULE("objectLiteral", () => {
      $.CONSUME(LBrace);
      $.OPTION(() => {
        $.SUBRULE($.objectPair);
        $.MANY(() => { $.CONSUME(Comma); $.SUBRULE2($.objectPair); });
      });
      $.CONSUME(RBrace);
    });

    // ---- Generic param rules (reusable across cues) ----
    $.RULE("genericParam", () => {
      // Cue-name tokens double as param keys (pin:30, audio:true, scale:1.2…)
      $.OR1([
        { ALT: () => $.CONSUME(Identifier, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Osc, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(OscCtrl, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Pin, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Audio, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Fade, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Scale, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Rotate, { LABEL: "key" }) },
        // draw(stop:<prefix>) — "stop" and "pause" are keyword tokens too
        { ALT: () => $.CONSUME(Stop, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Pause, { LABEL: "key" }) },
        // "x" lexes as the size-pair token (45x24), so x: needs naming here
        { ALT: () => $.CONSUME(XParam, { LABEL: "key" }) },
        { ALT: () => $.CONSUME(Midi, { LABEL: "key" }) },
      ]);
      $.CONSUME(Colon);
      $.OR2([
        { GATE: () => $.LA(1).tokenType === LBrace, ALT: () => $.SUBRULE($.objectLiteral, { LABEL: "value" }) },
        // Prand(0.01, 0.1, inf)*0.7 -- the pattern's every value times 0.7 (+=k, -=k too)
        { GATE: () => $.LA(1).tokenType === PatternName, ALT: () => {
          $.SUBRULE($.patternCall, { LABEL: "value" });
          $.MANY(() => $.CONSUME2(RelLiteral, { LABEL: "patOp" }));
        } },
        { GATE: () => $.LA(1).tokenType === LBracket, ALT: () => $.SUBRULE($.arrayValue, { LABEL: "value" }) },
        { GATE: () => ($.LA(1).tokenType === Identifier || $.LA(1).tokenType === Midi) && $.LA(2).tokenType === LParen, ALT: () => $.SUBRULE($.simpleFuncCall, { LABEL: "value" }) },
        // Size pairs like 45x24 (NumberLiteral + XParam + NumberLiteral)
        { GATE: () => $.LA(1).tokenType === NumberLiteral && $.LA(2).tokenType === XParam, ALT: () => $.SUBRULE($.sizePair, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(HexColor, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(OscAddr, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(RelLiteral, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(NumberLiteral, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(StringLiteral, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(NoteLiteral, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(RangeLiteral, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(True, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(False, { LABEL: "value" }) },
        { ALT: () => $.CONSUME1(Identifier, { LABEL: "value" }) },
        // a lone x is the size-pair token (45x24): a word where a value goes -- screen(mirror:x)
        { ALT: () => $.CONSUME1(XParam, { LABEL: "value" }) },
        // and "midi" the midi() cue's: trig:midi
        { ALT: () => $.CONSUME1(Midi, { LABEL: "value" }) },
        { ALT: () => $.CONSUME(MidiSpec, { LABEL: "value" }) },  // midi:cc64@1
      ]);
      // freq:Pwhite(40, 100) | scale(minor, root:A1) | harm(55)
      $.MANY2(() => { $.CONSUME(Pipe); $.SUBRULE($.pipeFilter, { LABEL: "pipe" }); });
    });

    // a pitch filter after | : scale(minor, root:A1), edo(19), sieve(3@0 + 4@1)
    $.RULE("pipeFilter", () => {
      $.OR([
        { ALT: () => $.CONSUME(Identifier, { LABEL: "fname" }) },
        { ALT: () => $.CONSUME(Scale, { LABEL: "fname" }) },        // scale is a cue word too
      ]);
      $.CONSUME(LParen);
      $.MANY_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.pipeArg) });
      $.CONSUME(RParen);
    });

    $.RULE("pipeArg", () => {
      $.OR([
        { ALT: () => $.CONSUME(SieveLiteral) },
        { GATE: () => $.LA(2).tokenType === Colon, ALT: () => {
          $.CONSUME2(Identifier, { LABEL: "argKey" });
          $.CONSUME(Colon);
          $.SUBRULE($.animValue, { LABEL: "argVal" });
        } },
        { ALT: () => $.SUBRULE2($.animValue, { LABEL: "arg" }) },
      ]);
    });

    $.RULE("genericParamList", () => {
      $.CONSUME(LParen);
      $.MANY_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) });
      $.OPTION(() => $.CONSUME(Comma));
      $.CONSUME(RParen);
    });

    // viz(s3, full:1) / hush(s2): a cue that addresses a uid may name it
    // bare, first -- viz(s3, …) for viz(uid:s3, …).
    $.RULE("uidParamList", () => {
      $.CONSUME(LParen);
      $.OPTION({
        GATE: () => $.LA(1).tokenType === Identifier && $.LA(2).tokenType !== Colon,
        DEF: () => {
          $.CONSUME(Identifier, { LABEL: "bareUid" });
          $.OPTION2(() => $.CONSUME(Comma));
        }
      });
      $.MANY_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) });
      $.OPTION3(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // hush(s2, 6): after the bare name, a bare number is the release --
    // hush(s2, 6) for hush(uid:s2, rel:6)
    $.RULE("hushParamList", () => {
      $.CONSUME(LParen);
      $.OPTION({
        GATE: () => $.LA(1).tokenType === Identifier && $.LA(2).tokenType !== Colon,
        DEF: () => {
          $.CONSUME(Identifier, { LABEL: "bareUid" });
          $.OPTION2(() => $.CONSUME(Comma));
          $.OPTION4({
            GATE: () => $.LA(1).tokenType === NumberLiteral && $.LA(2).tokenType !== Colon,
            DEF: () => {
              $.CONSUME(NumberLiteral, { LABEL: "bareRel" });
              $.OPTION5(() => $.CONSUME2(Comma));
            }
          });
        }
      });
      $.MANY_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) });
      $.OPTION3(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    $.RULE("genericParamListNoParens", () => {
      $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) });
    });

    // ---- Utility rules ----
    $.RULE("sizePair", () => {
      $.CONSUME(NumberLiteral, { LABEL: "width" });
      $.CONSUME(XParam);
      $.CONSUME1(NumberLiteral, { LABEL: "height" });
    });

    $.RULE("pageWithDuration", () => {
      $.CONSUME(Identifier, { LABEL: "page" });
      $.CONSUME(Colon);
      $.CONSUME(NumberLiteral, { LABEL: "dur" });
    });

    $.RULE("simpleFuncCall", () => {
      // midi(60) — "midi" is also the midi() cue keyword
      const name = $.OR([
        { ALT: () => $.CONSUME(Identifier) },
        { ALT: () => $.CONSUME(Midi) },
      ]).image;
      $.CONSUME(LParen);
      const args = [];
      $.OPTION(() => {
        args.push($.SUBRULE($.animValue));
        $.MANY(() => { $.CONSUME(Comma); args.push($.SUBRULE2($.animValue)); });
      });
      $.CONSUME(RParen);
      return { type: "funcCall", name, args };
    });

    // ---- Nested synth call (for patterns of synth configs) ----
    // Allows: Prand([synth(wave:sine, freq:~A4), synth(wave:square, freq:~C3)], 5)
    $.RULE("synthCallExpr", () => {
      $.CONSUME(Synth);
      $.SUBRULE($.genericParamList);
    });

    // ---- Pattern rules ----
    $.RULE("patternExpr", () => {
      $.OR([
        { GATE: () => $.LA(1).tokenType.name === "PatternName", ALT: () => $.SUBRULE($.patternCall) },
        // Nested synth call: synth(wave:sine, freq:~A4, ...)
        { GATE: () => $.LA(1).tokenType.name === "Synth", ALT: () => $.SUBRULE($.synthCallExpr) },
        { GATE: () => $.LA(1).tokenType.name === "Identifier" && $.LA(2).tokenType.name === "Colon", ALT: () => $.SUBRULE($.pageWithDuration) },
        { GATE: () => ["NumberLiteral", "NoteLiteral", "HexColor"].includes($.LA(1).tokenType.name) && $.LA(2).tokenType.name === "Colon", ALT: () => $.SUBRULE($.weightedValue) },
        // Array literal for chords within patterns: [~C4, ~E4, ~G4]
        { GATE: () => $.LA(1).tokenType.name === "LBracket", ALT: () => $.SUBRULE($.patternArrayLiteral) },
        // rand()/irand()/birand()/hrand() inside patterns and chords
        { GATE: () => ($.LA(1).tokenType.name === "Identifier" || $.LA(1).tokenType.name === "Midi") && $.LA(2).tokenType.name === "LParen", ALT: () => $.SUBRULE($.simpleFuncCall) },
        { ALT: () => $.CONSUME(NoteLiteral) },
        // Hex colors inside patterns: Pseq(#ff0000, #0000ff, inf)
        { ALT: () => $.CONSUME(HexColor) },
        { ALT: () => $.CONSUME(Identifier) },
        // a lone x is the size token (45x24): in a pattern it is a word --
        // axis:Pchoose(x, y, z)
        { ALT: () => $.CONSUME(XParam) },
        { ALT: () => $.CONSUME(NumberLiteral) },
        { ALT: () => { $.CONSUME(LParen); $.SUBRULE2($.patternExpr); $.CONSUME(RParen); } },
      ]);
    });

    // Array literal within patterns (for chords)
    $.RULE("patternArrayLiteral", () => {
      $.CONSUME(LBracket);
      $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.patternExpr) });
      $.CONSUME(RBracket);
    });

    // pwrand(0.15:1, 0:5, inf) -- a value and how likely it is (a word's,
    // pwrand(soft:3, loud:1), arrives as pageWithDuration)
    $.RULE("weightedValue", () => {
      $.OR([
        { ALT: () => $.CONSUME(NumberLiteral, { LABEL: "item" }) },
        { ALT: () => $.CONSUME(NoteLiteral, { LABEL: "item" }) },
        { ALT: () => $.CONSUME(HexColor, { LABEL: "item" }) },
      ]);
      $.CONSUME(Colon);
      $.CONSUME2(NumberLiteral, { LABEL: "weight" });
    });

    $.RULE("patternCall", () => {
      $.CONSUME(PatternName);
      $.CONSUME(LParen);
      // All values are flat - arrays like [~C4, ~E4, ~G4] are handled by patternArrayLiteral in patternExpr
      $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.patternExpr, { LABEL: "flatValues" }) });
      $.CONSUME(RParen);
    });

    // ---- Control/after clause rules ----
    $.RULE("controlExpr", () => {
      $.OR([
        { ALT: () => $.CONSUME(Nav, { LABEL: "controlName" }) },
        { ALT: () => $.CONSUME(Identifier, { LABEL: "controlName" }) },
      ]);
      $.CONSUME(LParen);
      $.CONSUME1(Identifier, { LABEL: "controlArg" });
      $.OPTION(() => { $.CONSUME(At, { LABEL: "At" }); $.CONSUME2(Identifier, { LABEL: "targetUid" }); });
      $.CONSUME(RParen);
    });

    $.RULE("afterClause", () => {
      $.CONSUME(After);
      $.CONSUME(Colon);
      $.OR([
        { ALT: () => $.SUBRULE($.controlExpr, { LABEL: "afterAction" }) },
        { ALT: () => $.CONSUME(Identifier, { LABEL: "afterSimple" }) }
      ]);
    });

    // ============================================================
    // CUE-SPECIFIC GRAMMAR RULES
    // ============================================================

    // ---- fade ----
    $.RULE("fadeParam", () => {
      $.OR([
        { ALT: () => $.CONSUME(Identifier, { LABEL: "keyIdent" }) },
        { ALT: () => $.CONSUME(Pin, { LABEL: "keyIdent" }) },
        { ALT: () => $.CONSUME(Osc, { LABEL: "keyIdent" }) },     // osc:1 — "osc" is a keyword token
        { ALT: () => $.CONSUME(Midi, { LABEL: "keyIdent" }) },    // midi:cc64, as "midi" is too
      ]);
      $.CONSUME(Colon);
      $.OR1([
        // follow(...) — gated the same way videoParam gates it, so a bare
        // Identifier is still an Identifier and only "name(" takes this branch
        {
          GATE: () => $.LA(1).tokenType.name === "Identifier" && $.LA(2).tokenType.name === "LParen",
          ALT: () => $.SUBRULE($.simpleFuncCall, { LABEL: "call" })
        },
        { ALT: () => $.CONSUME(NumberLiteral, { LABEL: "num" }) },
        { ALT: () => $.CONSUME(OscAddr, { LABEL: "addr" }) },
        { ALT: () => $.CONSUME(StringLiteral, { LABEL: "str" }) },
        { ALT: () => $.CONSUME1(Identifier, { LABEL: "ident" }) },
        { ALT: () => $.CONSUME(Stop, { LABEL: "kwStop" }) },
        { ALT: () => $.CONSUME2(Midi, { LABEL: "ident" }) },      // trig:midi
        { ALT: () => $.CONSUME(MidiSpec, { LABEL: "ident" }) },   // midi:cc64@1
      ]);
    });

    $.RULE("fadeParamList", () => {
      $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.fadeParam) });
    });

    $.RULE("cueFadeTop", () => {
      $.CONSUME(Fade);
      $.CONSUME(LParen);
      $.OPTION(() => $.SUBRULE($.fadeParamList));
      $.CONSUME(RParen);
    });

    // ---- page ----
    $.RULE("cuePageTop", () => {
      $.CONSUME(Page);
      $.CONSUME(LParen);
      $.SUBRULE($.pageBody, { LABEL: "body" });
      $.CONSUME(RParen);
    });

    $.RULE("pageBody", () => {
      $.SUBRULE($.patternExpr, { LABEL: "pattern" });
      $.MANY(() => {
        $.CONSUME(Comma);
        $.OR([
          { ALT: () => $.SUBRULE($.afterClause, { LABEL: "afterClause" }) },
          { ALT: () => $.SUBRULE($.genericParam, { LABEL: "param" }) },
        ]);
      });
    });

    // ---- video (has special sizePair support) ----
    this.RULE("cueVideoTop", () => {
      this.CONSUME(Video);
      this.CONSUME(LParen);
      this.OPTION(() => this.SUBRULE(this.videoParamList));
      this.CONSUME(RParen);
    });

    this.RULE("videoParamList", () => {
      this.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => this.SUBRULE(this.videoParam) });
    });

    this.RULE("videoParam", () => {
      // audio: and pin: are cue-name tokens, not Identifiers
      this.OR1([
        { ALT: () => this.CONSUME(Identifier, { LABEL: "key" }) },
        { ALT: () => this.CONSUME(Audio, { LABEL: "key" }) },
        { ALT: () => this.CONSUME(Pin, { LABEL: "key" }) },
      ]);
      this.CONSUME(Colon);
      this.OR2([
        { ALT: () => this.SUBRULE(this.sizePair) },
        // lists, Pseq/Prand patterns and rand() — drawn per grain (in:/seg:/speed:)
        { ALT: () => this.SUBRULE(this.arrayValue) },
        { GATE: () => this.LA(1).tokenType === PatternName, ALT: () => this.SUBRULE(this.patternCall) },
        {
          GATE: () => this.LA(1).tokenType.name === "Identifier" && this.LA(2).tokenType.name === "LParen",
          ALT: () => this.SUBRULE(this.simpleFuncCall)
        },
        { ALT: () => this.CONSUME(NumberLiteral) },
        { ALT: () => this.CONSUME(StringLiteral) },
        { ALT: () => this.CONSUME1(Identifier) }
      ]);
    });

    // ---- simple keyword + genericParamList cues ----
    $.RULE("cueTextTop", () => { $.CONSUME(Text); $.SUBRULE($.genericParamList); });
    $.RULE("cueAudioTop", () => { $.CONSUME(Audio); $.SUBRULE($.genericParamList); });
    $.RULE("cueAudioPoolTop", () => { $.CONSUME(AudioPool); $.SUBRULE($.genericParamList); });
    $.RULE("cueAudioImpulseTop", () => { $.CONSUME(AudioImpulse); $.SUBRULE($.genericParamList); });
    $.RULE("cueSynthTop", () => { $.CONSUME(Synth); $.SUBRULE($.genericParamList); });
    $.RULE("cueOscCtrlTop", () => { $.CONSUME(OscCtrl); $.SUBRULE($.genericParamList); });
    $.RULE("cueControlXYTop", () => { $.CONSUME(ControlXY); $.SUBRULE($.genericParamList); });

    $.RULE("cueAdcTop", () => {
      $.CONSUME(Adc);
      $.CONSUME(LParen);
      $.OPTION(() => { $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) }); });
      $.CONSUME(RParen);
    });

    // use(orion, …): a bare first name is the file (src:) -- the live
    // console's form (cues/useLive.js); use(path:…) as before
    $.RULE("cueUseTop", () => {
      $.CONSUME(Use);
      $.SUBRULE($.uidParamList);
    });

    $.RULE("cueRandomTop", () => {
      $.CONSUME(Random);
      $.CONSUME(LParen);
      $.OPTION(() => { $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) }); });
      $.CONSUME(RParen);
    });

    $.RULE("cueStopwatchTop", () => {
      $.CONSUME(Stopwatch);
      $.CONSUME(LParen);
      $.OPTION(() => { $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) }); });
      $.CONSUME(RParen);
    });

    // ---- osc (has trailing param list) ----
    $.RULE("trailingParamList", () => {
      $.SUBRULE($.genericParam);
      $.MANY(() => { $.CONSUME(Comma); $.SUBRULE2($.genericParam); });
    });

    $.RULE("cueOscTop", () => {
      $.CONSUME(Osc);
      $.SUBRULE($.genericParamList, { LABEL: "genericParamList" });
      $.OPTION(() => { $.CONSUME(Comma); $.SUBRULE($.trailingParamList, { LABEL: "trailingParamList" }); });
    });

    $.RULE("cueMidiTop", () => {
      $.CONSUME(Midi);
      $.SUBRULE($.genericParamList, { LABEL: "genericParamList" });
    });

    // ---- speed (shorthand number support) ----
    $.RULE("cueSpeedTop", () => {
      $.CONSUME(Identifier, { LABEL: "speedFn" });
      $.CONSUME(LParen);
      $.OPTION(() => {
        $.OR([
          {
            ALT: () => {
              $.CONSUME(NumberLiteral, { LABEL: "shorthand" });
              $.OPTION1(() => {
                $.CONSUME(Comma);
                $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) });
              });
            }
          },
          {
            ALT: () => {
              $.AT_LEAST_ONE_SEP1({ SEP: Comma, DEF: () => $.SUBRULE1($.genericParam) });
            }
          }
        ]);
      });
      $.CONSUME(RParen);
    });

    // ---- nav ----
    $.RULE("cueNavTop", () => {
      $.CONSUME(Nav);
      $.CONSUME(LParen);
      $.CONSUME(Identifier, { LABEL: "navAction" });
      $.OPTION(() => { $.CONSUME(At); $.CONSUME1(Identifier, { LABEL: "navTarget" }); });
      $.OPTION1(() => { $.CONSUME(Comma); $.SUBRULE($.genericParamListNoParens, { LABEL: "params" }); });
      $.CONSUME(RParen);
    });

    // ---- play / rewind / seek: the transport, by name ----
    // Plain identifiers behind a gate, not keyword tokens: a keyword claims
    // its word everywhere, and play: is already a parameter (stop(play:1)).
    // seek takes one number -- seconds, or with % a share of the score; a
    // sign makes it relative to where the playhead is.
    $.RULE("cueTransportTop", () => {
      $.CONSUME(Identifier, { LABEL: "transportFn" });
      $.CONSUME(LParen);
      $.OPTION(() => {
        $.CONSUME(NumberLiteral, { LABEL: "amount" });
        $.OPTION1(() => $.CONSUME(Percent, { LABEL: "percent" }));
      });
      $.CONSUME(RParen);
    });

    // ---- stop ----
    this.RULE("cueStopTop", () => {
      this.CONSUME(Stop);
      this.OPTION(() => {
        this.CONSUME(LParen);
        this.OPTION1(() => {
          this.SUBRULE(this.genericParam);
          this.MANY(() => { this.CONSUME(Comma); this.SUBRULE2(this.genericParam); });
        });
        this.CONSUME(RParen);
      });
    });

    // ---- pause ----
    $.RULE("cuePauseTop", () => {
      $.CONSUME(Pause);
      $.CONSUME(LParen);
      $.OR([
        { ALT: () => $.CONSUME(NumberLiteral, { LABEL: "shorthandDur" }) },
        { ALT: () => $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) }) }
      ]);
      $.CONSUME(RParen);
    });

    // ---- pin (standalone) ----
    // pin(30) · pin(30, edge:start, uid:box) · pin(edge:left, uid:box) · pin(release:box)
    $.RULE("cuePinTop", () => {
      $.CONSUME(Pin);
      $.CONSUME(LParen);
      $.OR([
        { ALT: () => {
            $.CONSUME(NumberLiteral, { LABEL: "shorthandDur" });
            $.MANY(() => { $.CONSUME(Comma); $.SUBRULE($.genericParam); });
        } },
        // pin(now): a word first -- held where it is, from now (engage:now)
        { GATE: () => $.LA(1).tokenType === Identifier && $.LA(2).tokenType !== Colon,
          ALT: () => {
            $.CONSUME(Identifier, { LABEL: "pinWord" });
            $.MANY2(() => { $.CONSUME2(Comma); $.SUBRULE3($.genericParam); });
        } },
        { ALT: () => $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE2($.genericParam) }) }
      ]);
      $.CONSUME(RParen);
    });

    // ---- image ----
    // image(src:…) / image(path:…, mode:…) — gated on the identifier, like
    // metro, so "image" stays an ordinary word everywhere else in the DSL
    $.RULE("cueImageTop", () => {
      $.CONSUME(Identifier, { LABEL: "imageName" });
      $.SUBRULE($.genericParamList);
    });

    // ---- metronome ----
    $.RULE("cueMetronomeTop", () => {
      $.CONSUME(Identifier, { LABEL: "metronomeName" });
      $.SUBRULE($.genericParamList);
    });

    // ---- oscOut / display (signal sinks) ----
    // oscOut(addr:/x, v: follow(a.vel)) · display(v: follow(a.vel), fmt:"{v}")
    // One rule, gated on the identifier like image and metro; the name
    // decides the AST type.
    $.RULE("cueSinkTop", () => {
      $.CONSUME(Identifier, { LABEL: "sinkName" });
      $.SUBRULE($.genericParamList);
    });

    // ---- capture (record the window and its sound, capture.js) ----
    // Gated on the identifier like url: capture() / capture(stop) / capture(size:720 ...)
    $.RULE("cueCaptureTop", () => {
      $.CONSUME(Identifier, { LABEL: "captureName" });
      $.CONSUME(LParen);
      $.OPTION(() => {
        $.OR([
          // a bare word first: capture(stop) -- stop is the stop() cue's token
          { GATE: () => $.LA(1).tokenType === Stop && $.LA(2).tokenType !== Colon,
            ALT: () => $.CONSUME(Stop, { LABEL: "bareWord" }) },
          { GATE: () => $.LA(1).tokenType === Identifier && $.LA(2).tokenType !== Colon,
            ALT: () => $.CONSUME2(Identifier, { LABEL: "bareWord" }) },
          { ALT: () => $.SUBRULE($.genericParam) },
        ]);
        $.MANY(() => { $.CONSUME(Comma); $.SUBRULE2($.genericParam); });
      });
      $.OPTION2(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- vu (meters, vu.js) ----
    // Gated on the identifier like capture: vu() / vu(bass, drums) / vu(all, floor:-48) / vu(stop)
    $.RULE("cueVuTop", () => {
      $.CONSUME(Identifier, { LABEL: "vuName" });
      $.CONSUME(LParen);
      $.MANY_SEP({
        SEP: Comma,
        DEF: () => $.OR([
          // a bare word: a sound's name, master, all -- or stop (the stop() cue's token)
          { GATE: () => $.LA(1).tokenType === Stop && $.LA(2).tokenType !== Colon,
            ALT: () => $.CONSUME(Stop, { LABEL: "bareWord" }) },
          { GATE: () => $.LA(1).tokenType === Identifier && $.LA(2).tokenType !== Colon,
            ALT: () => $.CONSUME2(Identifier, { LABEL: "bareWord" }) },
          { ALT: () => $.SUBRULE($.genericParam) },
        ]),
      });
      $.OPTION(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- level / xfade (a sound's fader, audio/level.js) ----
    // Gated on the identifier like capture: level(noise, 0, 12) / level([a, b], 0.5, 8, exp3)
    // / level(all, 0, 24) / xfade(noise, bass, 8)
    $.RULE("cueLevelTop", () => {
      $.CONSUME(Identifier, { LABEL: "levelName" });
      $.CONSUME(LParen);
      $.MANY_SEP({
        SEP: Comma,
        DEF: () => $.OR([
          { GATE: () => $.LA(2).tokenType === Colon, ALT: () => $.SUBRULE($.genericParam) },
          { GATE: () => $.LA(1).tokenType === LBracket, ALT: () => $.SUBRULE($.arrayValue, { LABEL: "bareList" }) },
          // a pattern or a function: bang(s, Pwhite(1, 5, inf)), bang(s, rand(1, 4))
          {
            GATE: () => $.LA(1).tokenType === PatternName || ($.LA(1).tokenType === Identifier && $.LA(2).tokenType === LParen),
            ALT: () => $.SUBRULE($.animValue, { LABEL: "bareValue" })
          },
          { ALT: () => $.CONSUME2(Identifier, { LABEL: "bareWord" }) },
          { ALT: () => $.CONSUME(NumberLiteral, { LABEL: "bareNum" }) },
          { ALT: () => $.CONSUME(RelLiteral, { LABEL: "bareRel" }) },
        ]),
      });
      $.OPTION(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- say (speech, cues/say.js) ----
    // say("bro what") / say(v1, "no shot", amp:0.8) / say("hi", voice:lessac, rate:0.8)
    $.RULE("cueSayTop", () => {
      $.CONSUME(Identifier, { LABEL: "sayName" });
      $.CONSUME(LParen);
      $.MANY_SEP({
        SEP: Comma,
        DEF: () => $.OR([
          { GATE: () => $.LA(2).tokenType === Colon, ALT: () => $.SUBRULE($.genericParam) },
          { ALT: () => $.CONSUME(StringLiteral, { LABEL: "bareText" }) },
          { ALT: () => $.CONSUME2(Identifier, { LABEL: "bareWord" }) },
        ]),
      });
      $.OPTION(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- scatter (throw what is drawn about, cues/scatter.js) ----
    // scatter(0.4) / scatter(0.4, dur:2) / scatter(follow(bass.level, 0, 0.6))
    $.RULE("cueScatterTop", () => {
      $.CONSUME(Identifier, { LABEL: "scatterName" });
      $.CONSUME(LParen);
      $.MANY_SEP({
        SEP: Comma,
        DEF: () => $.OR([
          { GATE: () => $.LA(2).tokenType === Colon, ALT: () => $.SUBRULE($.genericParam) },
          { ALT: () => $.SUBRULE($.animValue, { LABEL: "amount" }) },
        ]),
      });
      $.OPTION(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- strobe (flashing on the beat, strobe.js) ----
    // strobe(8) / strobe(8, duty:0.25, color:#000) / strobe(off) / x > strobe(16)
    $.RULE("cueStrobeTop", () => {
      $.CONSUME(Identifier, { LABEL: "strobeName" });
      $.CONSUME(LParen);
      $.MANY_SEP({
        SEP: Comma,
        DEF: () => $.OR([
          { GATE: () => $.LA(2).tokenType === Colon, ALT: () => $.SUBRULE($.genericParam) },
          { GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "off", ALT: () => $.CONSUME2(Identifier, { LABEL: "bareWord" }) },
          { ALT: () => $.SUBRULE($.animValue, { LABEL: "amount" }) },
        ]),
      });
      $.OPTION(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- grid (the stage in columns and rows, stageGrid.js) ----
    // grid(3, 2) / grid(4) / grid(cols:[2, 1], rows:2) / grid(shuffle) / grid(turn:-1) / grid(off)
    $.RULE("cueGridTop", () => {
      $.CONSUME(Identifier, { LABEL: "gridName" });
      $.CONSUME(LParen);
      $.MANY_SEP({
        SEP: Comma,
        DEF: () => $.OR([
          { GATE: () => $.LA(2).tokenType === Colon, ALT: () => $.SUBRULE($.genericParam) },
          { GATE: () => $.LA(1).tokenType === Identifier, ALT: () => $.CONSUME2(Identifier, { LABEL: "bareWord" }) },
          { ALT: () => $.SUBRULE($.animValue, { LABEL: "count" }) },
        ]),
      });
      $.OPTION(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- cam (the camera, on the stage and as signals, cam.js) ----
    // cam() / cam(uid:me, at:[0.75, 0.3], size:0.3) / cam(show:0) / cam(off) / cam(me, cell:5)
    $.RULE("cueCamTop", () => {
      $.CONSUME(Identifier, { LABEL: "camName" });
      $.CONSUME(LParen);
      $.MANY_SEP({
        SEP: Comma,
        DEF: () => $.OR([
          { GATE: () => $.LA(2).tokenType === Colon, ALT: () => $.SUBRULE($.genericParam) },
          { ALT: () => $.CONSUME2(Identifier, { LABEL: "bareWord" }) },
        ]),
      });
      $.OPTION(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- scene (a #$ block of the console, gone to: liveScenes.js) ----
    // scene(drop) / scene(drop, fade:8) / scene(drop, cut) / scene()
    // console(s1, perform) / console(close): the same form
    $.RULE("cueSceneTop", () => {
      $.CONSUME(Identifier, { LABEL: "sceneName" });
      $.CONSUME(LParen);
      $.MANY_SEP({
        SEP: Comma,
        DEF: () => $.OR([
          { GATE: () => $.LA(2).tokenType === Colon, ALT: () => $.SUBRULE($.genericParam) },
          { ALT: () => $.CONSUME2(Identifier, { LABEL: "bareWord" }) },
        ]),
      });
      $.OPTION(() => $.CONSUME1(Comma));
      $.CONSUME(RParen);
    });

    // ---- url (an element that is a link, url.js) ----
    // Gated on the identifier like the sinks: "url" is a common word in ids.
    $.RULE("cueUrlTop", () => {
      $.CONSUME(Identifier, { LABEL: "urlName" });
      $.SUBRULE($.genericParamList);
    });

    // ---- button ----
    // Supports: button(trigger:<cue>, mode:overlay, style(...))
    $.RULE("cueButtonTop", () => {
      $.CONSUME(Button);
      $.CONSUME(LParen);
      $.OPTION(() => {
        $.CONSUME(Identifier, { LABEL: "labelKey" });
        $.CONSUME(Colon);
        $.CONSUME(StringLiteral, { LABEL: "labelValue" });
        $.CONSUME(Comma);
      });
      $.CONSUME2(Identifier, { LABEL: "triggerKey" });
      $.CONSUME2(Colon);
      $.SUBRULE($.cueTop, { LABEL: "triggerValue" });
      // Optional direct params like mode:overlay
      $.MANY(() => {
        $.CONSUME2(Comma);
        $.SUBRULE($.genericParam, { LABEL: "buttonParam" });
      });
      // Optional style(...) block
      $.OPTION2(() => {
        $.CONSUME3(Comma);
        $.CONSUME3(Identifier, { LABEL: "styleKey" });
        $.CONSUME2(LParen);
        $.AT_LEAST_ONE_SEP({ SEP: Comma, DEF: () => $.SUBRULE2($.genericParam, { LABEL: "styleParam" }) });
        $.CONSUME(RParen);
      });
      $.CONSUME2(RParen);
    });

    // ---- animation cues ----
    $.RULE("cueRotateTop", () => { $.CONSUME(Rotate); $.SUBRULE($.animGenericParamList); });
    $.RULE("cueTransTop", () => { $.CONSUME(Trans); $.SUBRULE($.animGenericParamList); });
    $.RULE("cueScaleTop", () => {
      $.OR([{ ALT: () => $.CONSUME(Scale) }, { ALT: () => $.CONSUME2(ScaleXY) }]);
      $.SUBRULE($.animGenericParamList);
    });
    $.RULE("cueO2PTop", () => { $.CONSUME(O2P); $.SUBRULE($.animGenericParamList); });
    $.RULE("cueColorTop", () => { $.CONSUME(Color); $.SUBRULE($.animGenericParamList); });

    // ---- notation (static visual — rendered at load) ----
    $.RULE("cueNotationTop", () => { $.CONSUME(Notation); $.SUBRULE($.genericParamList); });

    // ---- ext (extension function call) ----
    $.RULE("cueExtTop", () => { $.CONSUME(Ext); $.SUBRULE($.genericParamList); });

    // ---- draw (ink left behind by whatever moves the element) ----
    $.RULE("cueDrawTop", () => { $.CONSUME(Draw); $.SUBRULE($.genericParamList); });

    // ---- zoom (move the view: zoom level + centre on an object) ----
    $.RULE("cueZoomTop", () => { $.CONSUME(Zoom); $.SUBRULE($.genericParamList); });

    // ---- hush (silence: everything, or the voices named by uid:) ----
    $.RULE("cueHushTop", () => { $.CONSUME(Hush); $.SUBRULE($.hushParamList); });

    // ---- tempo (the beat grid: tempo(120), tempo(bpm:90), tempo() says it) ----
    $.RULE("cueTempoTop", () => {
      $.CONSUME(Tempo);
      $.CONSUME(LParen);
      $.OPTION(() => $.CONSUME(NumberLiteral, { LABEL: "bpm" }));
      $.OPTION2(() => $.CONSUME(Comma));
      $.MANY_SEP({ SEP: Comma, DEF: () => $.SUBRULE($.genericParam) });
      $.CONSUME(RParen);
    });

    // ---- viz (the look of a uid's box or shape: full, at, size, fill...) ----
    $.RULE("cueVizTop", () => { $.CONSUME(Viz); $.SUBRULE($.uidParamList); });

    // ---- shape (a polygon made at run time: sides, size, at, fill...) ----
    $.RULE("cueShapeTop", () => { $.CONSUME(Shape); $.SUBRULE($.genericParamList); });

    // ---- station / leg (map mode: the route, see route.js) ----
    $.RULE("cueStationTop", () => { $.CONSUME(Station); $.SUBRULE($.genericParamList); });
    $.RULE("cueLegTop", () => { $.CONSUME(Leg); $.SUBRULE($.genericParamList); });

    // ---- ui (supports trigger: for nested cues) ----
    $.RULE("cueUiTop", () => {
      $.CONSUME(Ui);
      $.CONSUME(LParen);
      $.OPTION(() => { $.CONSUME(StringLiteral, { LABEL: "selector" }); });
      $.OPTION2(() => {
        $.OPTION3(() => $.CONSUME(Comma));
        $.MANY_SEP({
          SEP: Comma,
          DEF: () => $.SUBRULE($.uiParam)
        });
      });
      $.CONSUME(RParen);
    });

    // ui parameter - either trigger:cue or key:value
    $.RULE("uiParam", () => {
      $.OR([
        {
          // trigger:cue (nested cue)
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "trigger",
          ALT: () => {
            $.CONSUME(Identifier, { LABEL: "triggerKey" });
            $.CONSUME(Colon);
            $.SUBRULE($.cueTop, { LABEL: "triggerValue" });
          }
        },
        {
          // Regular key:value param
          ALT: () => $.SUBRULE($.genericParam, { LABEL: "param" })
        }
      ]);
    });

    // ============================================================
    // cueTop — main entry point
    // ============================================================
    $.RULE("cueTop", () => {
      $.OPTION(() => { $.CONSUME(Cue); $.CONSUME(Colon); });
      $.OR([
        { ALT: () => $.SUBRULE($.cueTextTop) },
        { ALT: () => $.SUBRULE($.cueFadeTop) },
        { ALT: () => $.SUBRULE($.cueNavTop) },
        { ALT: () => $.SUBRULE($.cuePageTop) },
        { ALT: () => $.SUBRULE($.cueAdcTop) },
        { ALT: () => $.SUBRULE($.cueRandomTop) },
        { ALT: () => $.SUBRULE($.cueUseTop) },
        { ALT: () => $.SUBRULE($.cueStopwatchTop) },
        { ALT: () => $.SUBRULE($.cueVideoTop) },
        {
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "image",
          ALT: () => $.SUBRULE($.cueImageTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier &&
            ($.LA(1).image === "metro" || $.LA(1).image === "metronome"),
          ALT: () => $.SUBRULE($.cueMetronomeTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier &&
            ($.LA(1).image === "oscOut" || $.LA(1).image === "display"),
          ALT: () => $.SUBRULE($.cueSinkTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "url",
          ALT: () => $.SUBRULE($.cueUrlTop)
        },
        {
          // capture() and resources() (system/resources.js) take the same form;
          // so does still() (still.js)
          GATE: () => $.LA(1).tokenType === Identifier && ["capture", "resources", "screen", "still"].includes($.LA(1).image)
            && $.LA(2).tokenType === LParen,
          ALT: () => $.SUBRULE($.cueCaptureTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "vu",
          ALT: () => $.SUBRULE($.cueVuTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "scatter" && $.LA(2).tokenType === LParen,
          ALT: () => $.SUBRULE($.cueScatterTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "grid" && $.LA(2).tokenType === LParen,
          ALT: () => $.SUBRULE($.cueGridTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "strobe" && $.LA(2).tokenType === LParen,
          ALT: () => $.SUBRULE($.cueStrobeTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "cam" && $.LA(2).tokenType === LParen,
          ALT: () => $.SUBRULE($.cueCamTop)
        },
        {
          // scenes(intro:16, drop:32, fade:4, loop:1): the same rule, a sequence of them;
          // console(s1, perform): the console opened on a scene (consoleHook.js)
          GATE: () => $.LA(1).tokenType === Identifier && ["scene", "scenes", "console"].includes($.LA(1).image) && $.LA(2).tokenType === LParen,
          ALT: () => $.SUBRULE($.cueSceneTop)
        },
        {
          // say("words") and style(agent, "css", zoom:12 ...) take the same form
          GATE: () => $.LA(1).tokenType === Identifier && ["say", "style"].includes($.LA(1).image) && $.LA(2).tokenType === LParen,
          ALT: () => $.SUBRULE($.cueSayTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier && ["level", "xfade", "hold", "gate", "seq", "bang", "fx", "group", "solo", "palette", "wait", "speakers"].includes($.LA(1).image)
            && $.LA(2).tokenType === LParen,
          ALT: () => $.SUBRULE($.cueLevelTop)
        },
        { ALT: () => $.SUBRULE($.cuePauseTop) },
        { ALT: () => $.SUBRULE($.cuePinTop) },
        { ALT: () => $.SUBRULE($.cueUiTop) },
        { ALT: () => $.SUBRULE($.cueExtTop) },
        { ALT: () => $.SUBRULE($.cueDrawTop) },
        { ALT: () => $.SUBRULE($.cueZoomTop) },
        { ALT: () => $.SUBRULE($.cueHushTop) },
        { ALT: () => $.SUBRULE($.cueTempoTop) },
        { ALT: () => $.SUBRULE($.cueShapeTop) },
        { ALT: () => $.SUBRULE($.cueVizTop) },
        { ALT: () => $.SUBRULE($.cueStationTop) },
        { ALT: () => $.SUBRULE($.cueLegTop) },
        {
          GATE: () => $.LA(1).tokenType === Identifier && $.LA(1).image === "speed",
          ALT: () => $.SUBRULE($.cueSpeedTop)
        },
        {
          GATE: () => $.LA(1).tokenType === Identifier && TRANSPORT_WORDS.has($.LA(1).image),
          ALT: () => $.SUBRULE($.cueTransportTop)
        },
        { ALT: () => $.SUBRULE($.cueStopTop) },
        { ALT: () => $.SUBRULE($.cueButtonTop) },
        { ALT: () => $.SUBRULE($.cueAudioTop) },
        { ALT: () => $.SUBRULE($.cueAudioPoolTop) },
        { ALT: () => $.SUBRULE($.cueAudioImpulseTop) },
        { ALT: () => $.SUBRULE($.cueSynthTop) },
        { ALT: () => $.SUBRULE($.cueRotateTop) },
        { ALT: () => $.SUBRULE($.cueTransTop) },
        { ALT: () => $.SUBRULE($.cueScaleTop) },
        { ALT: () => $.SUBRULE($.cueO2PTop) },
        { ALT: () => $.SUBRULE($.cueColorTop) },
        { ALT: () => $.SUBRULE($.cueOscTop) },
        { ALT: () => $.SUBRULE($.cueMidiTop) },
        { ALT: () => $.SUBRULE($.cueOscCtrlTop) },
        { ALT: () => $.SUBRULE($.cueControlXYTop) },
        { ALT: () => $.SUBRULE($.cueNotationTop) },
      ]);
    });

    this.performSelfAnalysis();
  }
}

// ============================================================================
// 3️⃣ UNIFIED VALUE EXTRACTION HELPERS
// ============================================================================

/** A pattern with *k / +=k after it: its ops, applied to every value it draws (audioShared). */
function withPatternOps(val, opTokens) {
  if (!opTokens?.length || val?.type !== "pattern") return val;
  return { ...val, ops: [...(val.ops || []), ...opTokens.map(t => relLiteral(t.image))] };
}

/**
 * Pwhite(40, 100) | scale(minor, root:A1): a pattern carries its filters
 * (pipe: [{ name, args, named }]) -- every value drawn goes through them;
 * anything else is wrapped: { type: "pipe", source, pipe }.
 */
function withPipe(val, nodes) {
  if (!nodes?.length) return val;
  const pipe = nodes.map(n => {
    const args = [], named = {};
    for (const a of n.children.pipeArg || []) {
      const c = a.children;
      if (c.SieveLiteral) args.push(c.SieveLiteral[0].image);
      else if (c.argKey) named[c.argKey[0].image] = extractVal(c.argVal[0]);
      else if (c.arg) args.push(extractVal(c.arg[0]));
    }
    return { name: n.children.fname[0].image, args, named };
  });
  return val?.type === "pattern" ? { ...val, pipe: [...(val.pipe || []), ...pipe] } : { type: "pipe", source: val, pipe };
}

/** *0.9 / *=0.9 -> { type: "rel", op: "*", by: 0.9 }; +=0.2 / -=0.2 -> op "+", by ±0.2 */
function relLiteral(image) {
  const m = /^(\*=?|[+\-]=)\s*(.+)$/.exec(image);
  const by = Number(m[2]);
  return m[1].startsWith("*") ? { type: "rel", op: "*", by } : { type: "rel", op: "+", by: m[1] === "-=" ? -by : by };
}

/** Unquote a string literal token image */
function unquote(s) {
  if (!s) return s;
  return s.replace(/^["']|["']$/g, "");
}

/** Parse a raw string into a typed JS value */
function parseRaw(raw) {
  if (raw === undefined || raw === null) return raw;
  const s = String(raw).trim();
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'")))
    return s.slice(1, -1);
  if (s === "true") return true;
  if (s === "false") return false;
  if (s === "inf") return "inf";
  if (!isNaN(s) && s !== "") return Number(s);
  return s;
}

/**
 * Universal value extractor — handles any CST value node.
 * Replaces: extractValue, extractValueExpr, extractObjectValue,
 *           inline parseVal, and duplicated token-unwrapping blocks.
 */
function extractVal(node) {
  if (!node) return null;

  // Direct token (has .image and .tokenType)
  if (node.image !== undefined && node.tokenType) {
    const tName = node.tokenType.name;
    if (tName === "NumberLiteral") return Number(node.image);
    if (tName === "RelLiteral") return relLiteral(node.image);
    if (tName === "StringLiteral") return unquote(node.image);
    if (tName === "NoteLiteral") return node.image;  // Note names like A#4, C3, Db5
    if (tName === "True") return true;
    if (tName === "False") return false;
    if (tName === "RangeLiteral") return node.image;
    if (node.image === "inf") return "inf";
    return node.image;
  }

  // Has .image but no .tokenType (raw token object)
  if (node.image !== undefined) {
    return parseRaw(node.image);
  }

  if (!node.children) return null;

  // Array literal (must check BEFORE animValue unwrap — arrayValue nodes also have animValue children)
  if (node.name === "arrayValue") {
    return (node.children.animValue || []).map(extractVal);
  }

  // Composite nodes that have animValue children as arguments — must check
  // BEFORE the generic animValue unwrap, otherwise the unwrap strips the
  // node down to just its first child value (e.g. rand(33,333) → 33).
  if (node.name === "simpleFuncCall") return extractFuncCallNode(node);
  if (node.name === "patternCall") return extractPatternCallNode(node);
  if (node.name === "objectLiteral") return extractObjectLiteralNode(node);

  // Unwrap animValue wrapper
  if (node.children.animValue) return extractVal(node.children.animValue[0]);
  if (node.name === "animValue") {
    for (const key of ["arrayValue", "patternCall", "simpleFuncCall",
      "HexColor", "NumberLiteral", "StringLiteral", "NoteLiteral", "OscAddr", "True", "False", "Identifier", "XParam", "Midi", "MidiSpec"]) {
      if (node.children[key]?.[0]) return extractVal(node.children[key][0]);
    }
  }

  // objectValue wrapper
  if (node.name === "objectValue") {
    for (const key of ["patternCall", "simpleFuncCall", "arrayValue", "RelLiteral",
      "NumberLiteral", "StringLiteral", "NoteLiteral", "True", "False", "Identifier"]) {
      if (node.children[key]?.[0]) return withPatternOps(extractVal(node.children[key][0]), node.children.patOp);
    }
  }

  // Nested cue (for button triggers etc)
  if (node.name === "cueTop") return cstToAst(node);

  // Size pair (WxH format like 45x24)
  if (node.name === "sizePair") {
    const w = node.children.width?.[0]?.image || "0";
    const h = node.children.height?.[0]?.image || "0";
    return `${w}x${h}`;
  }

  // Try unwrapping single child
  for (const key of ["NumberLiteral", "StringLiteral", "NoteLiteral", "OscAddr", "True", "False",
    "Identifier", "patternCall", "simpleFuncCall", "arrayValue", "objectLiteral", "sizePair", "XParam", "Midi", "MidiSpec"]) {
    if (node.children[key]?.[0]) return extractVal(node.children[key][0]);
  }

  return null;
}

/** Extract a synthCallExpr CST node into a synth config object */
function extractSynthCallExprNode(node) {
  // synthCallExpr has a genericParamList child
  const paramList = node.children?.genericParamList?.[0];
  if (!paramList) return { type: "synthConfig", args: {} };

  // Extract args as array [{type, value}, ...] then convert to object {key: value, ...}
  const argsArray = extractGenericArgs(paramList.children?.genericParam || []);
  const args = {};
  for (const a of argsArray) {
    if (a.type) args[a.type] = a.value;
  }
  return { type: "synthConfig", args };
}

/** Extract a patternCall CST node into an AST pattern object */
function extractPatternCallNode(node) {
  const name = patternName(node.children?.PatternName?.[0]?.image ?? "Pseq");
  const flatExprs = node.children?.flatValues || [];

  const getPatternVal = (expr) => {
    if (!expr) return null;
    if (expr.children?.patternCall?.[0]) return extractPatternCallNode(expr.children.patternCall[0]);
    // rand()/hrand() etc inside a pattern step or chord
    if (expr.children?.simpleFuncCall?.[0]) return extractFuncCallNode(expr.children.simpleFuncCall[0]);
    // Nested synth call: synth(wave:sine, freq:~A4, ...)
    if (expr.children?.synthCallExpr?.[0]) return extractSynthCallExprNode(expr.children.synthCallExpr[0]);
    if (expr.children?.patternArrayLiteral?.[0]) {
      // Array literal (chord) within pattern: [~C4, ~E4, ~G4]
      const arrNode = expr.children.patternArrayLiteral[0];
      const elements = arrNode.children?.patternExpr || [];
      return elements.map(getPatternVal);
    }
    if (expr.children?.pageWithDuration?.[0]) {
      const pwd = expr.children.pageWithDuration[0];
      return {
        page: pwd.children?.Identifier?.[0]?.image || pwd.children?.page?.[0]?.image,
        dur: Number(pwd.children?.NumberLiteral?.[0]?.image || pwd.children?.dur?.[0]?.image || 0)
      };
    }
    if (expr.children?.weightedValue?.[0]) {
      const wv = expr.children.weightedValue[0].children;
      const item = wv.item?.[0];
      return { item: item?.tokenType?.name === "NumberLiteral" ? Number(item.image) : item?.image, weight: Number(wv.weight?.[0]?.image) };
    }
    if (expr.children?.NumberLiteral?.[0]) return Number(expr.children.NumberLiteral[0].image);
    if (expr.children?.NoteLiteral?.[0]) return expr.children.NoteLiteral[0].image;  // Note names
    if (expr.children?.HexColor?.[0]) return expr.children.HexColor[0].image;        // #rrggbb in color patterns
    if (expr.children?.XParam?.[0]) return "x";
    if (expr.children?.Identifier?.[0]) {
      const id = expr.children.Identifier[0].image;
      return id === "inf" ? Infinity : id;
    }
    return null;
  };

  const allValues = flatExprs.map(getPatternVal);

  // Check if last value is a number (repeat count) - applies to Prand, Pxrand, Pshuf, Pseq
  // Syntax: Prand([val1, val2], 5) or Prand(val1, val2, 5) or Prand(val1, val2, inf)
  // peuclid(3, 8, rot:2) -- a pattern's own named option, out of its values
  // before the last number is read as the count
  const options = {};
  const named = v => v && typeof v === "object" && "page" in v && "dur" in v && PATTERN_OPTIONS[name]?.includes(v.page);
  let values = allValues.filter(v => !(named(v) && (options[v.page] = v.dur, true)));
  const counted = values;
  let repeats = Infinity;

  if (counted.length >= 2) {
    const lastVal = counted[counted.length - 1];
    // If last value is a plain number (including Infinity) and not a synthConfig or pattern, treat as repeat count
    if (typeof lastVal === "number") {
      repeats = lastVal;
      values = counted.slice(0, -1);
    }
  }

  // pwrand(0.15:1, 0:5, inf): the values and their weights, as the two
  // lists SuperCollider writes -- Pwrand([0.15, 0], [1, 5], inf)
  const paired = v => v && typeof v === "object" && ("weight" in v || ("page" in v && "dur" in v));
  if (name === "Pwrand" && values.length && values.every(paired)) {
    values = [values.map(v => "weight" in v ? v.item : v.page), values.map(v => "weight" in v ? v.weight : v.dur)];
  }

  return { type: "pattern", name, values, repeats, ...options };
}

/** Extract a simpleFuncCall CST node */
function extractFuncCallNode(node) {
  const fname = node.children?.Identifier?.[0]?.image ?? node.children?.Midi?.[0]?.image;
  const argsCst = node.children?.animValue || [];

  // For rand/irand/birand with exactly 2 numeric args, return typed funcCall
  if ((fname === "rand" || fname === "irand" || fname === "birand") && argsCst.length >= 2) {
    const args = argsCst.map(extractVal);
    const numArgs = args.filter(a => typeof a === "number");
    if (numArgs.length >= 2) {
      return { type: "funcCall", name: fname, args: numArgs.slice(0, 2) };
    }
  }

  return { type: "funcCall", name: fname, args: argsCst.map(extractVal) };
}

/** Extract an objectLiteral CST node into a plain object */
function extractObjectLiteralNode(node) {
  const result = {};
  for (const pair of (node.children?.objectPair || [])) {
    const key = pair.children?.key?.[0]?.image;
    const valNode = pair.children?.value?.[0];
    // follow() inside an object -- filter:{freq:follow(knob.t, 200, 8000)}
    // -- is a signal reference too, as it is at the top level
    if (key) result[key] = maybeConvertLink(extractVal(valNode));
  }
  return result;
}

// ============================================================================
// PATTERN NODE CONVERTER (for page cue patterns — uses patternExpr rule)
// ============================================================================
function convertPatternNodeToAST(node) {
  if (!node) return { type: "Literal", value: null };

  if (node.name === "patternExpr") {
    if (node.children?.patternCall) return convertPatternNodeToAST(node.children.patternCall[0]);
    if (node.children?.pageWithDuration) return convertPatternNodeToAST(node.children.pageWithDuration[0]);
    if (node.children?.NoteLiteral) return { type: "Literal", value: node.children.NoteLiteral[0].image };
    if (node.children?.Identifier) return { type: "Literal", value: node.children.Identifier[0].image };
    if (node.children?.NumberLiteral) return { type: "Literal", value: Number(node.children.NumberLiteral[0].image) };
  }

  if (node.name === "pageWithDuration") {
    const id = node.children.Identifier?.[0]?.image || node.children.page?.[0]?.image || null;
    const dur = node.children.NumberLiteral?.[0]?.image || node.children.dur?.[0]?.image || 0;
    return { type: "Literal", value: { page: id, dur: Number(dur) } };
  }

  if (node.name === "patternCall") {
    const name = patternName(node.children?.PatternName?.[0]?.image ?? "Pseq");
    // patternCall labels its subrule results as "flatValues" not "patternExpr"
    const flatValues = node.children.flatValues || node.children.patternExpr || [];

    // Pattern([ page1, page2, ... ], repeats) form — first arg is an array literal,
    // second arg (if present) is the repeats count.
    let list = [];
    let repeats = 1;

    if (flatValues.length > 0) {
      // Peek at first flatValue: does it wrap a patternArrayLiteral?
      const firstChild = flatValues[0];
      const arrLit = firstChild?.children?.patternArrayLiteral?.[0];
      if (arrLit) {
        // Unwrap the array items as the list
        list = (arrLit.children?.patternExpr || []).map(convertPatternNodeToAST);
        // Second flatValue is repeats
        if (flatValues.length > 1) {
          const r = convertPatternNodeToAST(flatValues[1]);
          if (typeof r?.value === "number") repeats = r.value;
          else if (r?.value === "inf" || r?.image === "inf") repeats = "inf";
        }
      } else {
        // Flat form: P(item, item, item, repeatsNum) — last NumberLiteral is repeats
        const converted = flatValues.map(convertPatternNodeToAST);
        const lastIdx = converted.length - 1;
        const lastVal = converted[lastIdx]?.value;
        if ((typeof lastVal === "number" || lastVal === "inf") && lastIdx > 0) {
          repeats = lastVal;
          list = converted.slice(0, lastIdx);
        } else {
          list = converted;
        }
      }
    }

    return { type: name, list, repeats };
  }

  if (node.children?.patternExpr) return convertPatternNodeToAST(node.children.patternExpr[0]);
  return { type: "Literal", value: node.image || null };
}

// ============================================================================
// AFTER CLAUSE EXTRACTOR
// ============================================================================
function extractAfterClause(children) {
  const clause = children?.afterClause?.[0];
  const action = clause?.children?.afterAction?.[0];
  const ctrl = action?.children?.controlExpr?.[0] || action;
  if (!ctrl?.children) return null;
  const control = ctrl.children.controlName?.[0]?.image || null;
  const arg = ctrl.children.controlArg?.[0]?.image || null;
  const target = ctrl.children.targetUid?.[0]?.image || null;
  if (!control) return null;
  return { control, arg, target };
}

/**
 * The cue a page sequence's after: runs when it ends. mode(scroll@F) is the
 * clause's own word for nav(scroll@F) (docs cues/cue_page.md), and no cue of
 * its own; any other name is the cue of that name, after:console(close).
 */
export function afterActionCue({ control, arg, target }) {
  return `${control === "mode" ? "nav" : control}(${arg}${target ? `@${target}` : ""})`;
}

// ============================================================================
// GENERIC PARAM EXTRACTION HELPERS
// ============================================================================

/**
 * Extract genericParam[] children into [{type, value}] array.
 */
function extractGenericArgs(paramNodes) {
  const args = [];
  for (const p of paramNodes) {
    const key = p.children.key?.[0]?.image;
    if (!key) continue;

    const valNode = p.children.value?.[0];
    let val = extractVal(valNode);
    // Fallback: try raw image
    if (val === null && valNode?.image != null) {
      val = parseRaw(valNode.image);
    }
    // Prand(...)*0.7: the ops after a pattern
    val = withPatternOps(val, p.children.patOp);
    // ... | scale(minor): the pitch filters after a value
    val = withPipe(val, p.children.pipe);

    // follow(...) → signalRef / oscRef. Unconditional: the call is explicit, so
    // there is no allowlist of "bindable" parameter names and no sniffing of
    // bare strings (which would turn filenames like clip.webm into bindings).
    val = maybeConvertLink(val);

    // Condition check for 'if' parameter - convert to condition object
    if (key === 'if' && typeof val === 'string') {
      val = maybeConvertToCondition(val);
    }

    args.push({ type: key, value: val });
  }
  return args;
}

/** What maybeConvertLink makes of a binding: a value that follows a source. */
const BINDING_TYPES = new Set(["signalRef", "oscRef", "combine"]);

/**
 * Extract genericParam[] into a flat {key: value} object.
 * Used by audio, audioPool, audioImpulse, notation and pause. With
 * bindings:true a follow(...) / map(...) / mul(...) call becomes a binding,
 * as in every other cue.
 */
function extractGenericParamsFlat(paramNodes, opts = {}) {
  const params = {};
  for (const p of paramNodes) {
    const key = p.children.key?.[0]?.image;
    if (!key) continue;

    const valNode = p.children.value?.[0];
    let val = extractVal(valNode);
    if (opts.bindings && valNode?.name === "simpleFuncCall") {
      const link = maybeConvertLink(val);
      if (BINDING_TYPES.has(link?.type)) val = link;
    }
    if (val === null && valNode?.image != null) {
      val = parseRaw(valNode.image);
    }

    // Condition check for 'if' parameter - convert to condition object
    if (key === 'if' && typeof val === 'string') {
      val = maybeConvertToCondition(val);
    }

    params[key] = val;
  }
  return params;
}

/** A uidParamList's args, with a bare first name as uid: (viz(s3, …)). */
function uidParamArgs(cstNode) {
  const list = cstNode.children?.uidParamList?.[0] || cstNode.children?.hushParamList?.[0];
  const args = extractGenericArgs(list?.children?.genericParam || []);
  const bare = list?.children?.bareUid?.[0]?.image;
  const rel = list?.children?.bareRel?.[0]?.image;
  if (rel != null && !args.some(a => a.type === "rel")) args.unshift({ type: "rel", value: Number(rel) });
  if (bare && !args.some(a => a.type === "uid")) args.unshift({ type: "uid", value: bare });
  return args;
}

/**
 * A param of the cues read flat (speed, pin, stop, nav): a plain token's
 * text as written, or a structured value (rand(), a pattern, follow()),
 * which has no text, extracted whole.
 */
function paramText(p) {
  const node = p.children.value?.[0];
  return node?.image !== undefined ? node.image : extractVal(node);
}
const isStructured = (v) => v !== null && typeof v === "object";
/** A number from a token's text; a structured value as it is, for the cue to roll. */
const numOr = (v) => (isStructured(v) ? v : Number(v));
/** parseRaw of a token's text; a structured value as it is. */
const rawOr = (v) => (isStructured(v) ? v : parseRaw(v));

/** Get genericParam nodes from a CST node that uses genericParamList. */
function getGenericParams(cstNode) {
  const list = cstNode.children?.genericParamList?.[0];
  return list?.children?.genericParam || [];
}

// ============================================================================
// ANIMATION HELPERS
// ============================================================================

/**
 * Extract animation KV args from an animation cue node.
 * Returns [{type, value}, ...] — same shape as v2.
 */
function extractAnimKvArgs(node) {
  const out = [];
  const paramList = node.children.animGenericParamList?.[0];
  if (!paramList) return out;

  // First positional value
  const firstValueNode = paramList.children?.firstValue?.[0];
  const secondValueNode = paramList.children?.secondValue?.[0];
  if (firstValueNode && secondValueNode) {
    // scaleXY(xs, ys): the two axes
    // (follow(...) there is a binding, as it is after a key)
    out.push({ type: "x", value: maybeConvertLink(extractVal(firstValueNode)) });
    out.push({ type: "y", value: maybeConvertLink(extractVal(secondValueNode)) });
  } else if (firstValueNode) {
    // scale(follow(bass.t, 0.1, 1)): the size follows a signal
    out.push({ type: "values", value: maybeConvertLink(extractVal(firstValueNode)) });
  }

  // Named key:value params
  const restParams = paramList.children?.restParams || paramList.children?.kvParams || [];
  for (const p of restParams) {
    const key = p.children.key?.[0]?.image || p.key;
    if (!key) continue;

    let vNode = p.children.value?.[0] || p.children.animValue?.[0] ||
      p.children.NumberLiteral?.[0] || p.children.StringLiteral?.[0] ||
      p.children.Identifier?.[0] || null;

    // Unwrap wrappers
    if (vNode?.children?.StringLiteral) vNode = vNode.children.StringLiteral[0];
    if (vNode?.children?.NumberLiteral) vNode = vNode.children.NumberLiteral[0];

    let val;
    if (vNode?.tokenType?.name === "StringLiteral") {
      val = unquote(vNode.image);
    } else {
      val = extractVal(vNode?.children
        ? { children: { ...(vNode.children || {}), [vNode.tokenType?.name]: [vNode] } }
        : vNode);
    }

    if (val === null || val === undefined) {
      const img = vNode?.image;
      if (img === "true") val = true;
      else if (img === "false") val = false;
      else if (img !== undefined && !isNaN(img)) val = Number(img);
      else val = img ?? null;
    }

    // follow(...) → signalRef / oscRef, on any parameter.
    val = maybeConvertLink(val);

    // Condition check for 'if' parameter - convert to condition object
    if (key === 'if' && typeof val === 'string') {
      val = maybeConvertToCondition(val);
    }

    out.push({ type: key, value: val });
  }

  return out;
}

// ============================================================================
// OSC-SPECIFIC FUNC CALL HANDLER
// ============================================================================
function extractOscFuncCall(valNode) {
  const fname = valNode.children?.Identifier?.[0]?.image?.toLowerCase();
  const argsCst = valNode.children?.animValue ?? [];

  const extractNum = (v) => {
    if (!v) return null;
    if (v.name === "animValue") {
      const child = v.children?.NumberLiteral?.[0] || v.children?.simpleFuncCall?.[0];
      return extractNum(child);
    }
    if (v.image != null && !isNaN(v.image)) return Number(v.image);
    return null;
  };

  if (fname === "deg" && argsCst.length === 2) {
    const degree = extractNum(argsCst[0]);
    const octave = extractNum(argsCst[1]);
    if (degree != null && octave != null) return { type: "deg", degree, octave };
  }
  if ((fname === "rand" || fname === "irand" || fname === "birand") && argsCst.length === 2) {
    const min = extractNum(argsCst[0]);
    const max = extractNum(argsCst[1]);
    if (Number.isFinite(min) && Number.isFinite(max)) return { type: fname, min, max };
  }
  if ((fname === "hz" || fname === "midi") && argsCst.length === 1) {
    const value = extractNum(argsCst[0]);
    if (Number.isFinite(value)) return { type: fname, value };
  }

  return { type: "func", name: fname, args: argsCst.map(extractNum) };
}

// ============================================================================
// 4️⃣ CST → AST  (data-driven, replaces ~1500 lines)
// ============================================================================

function cstToAst(cst) {

  // Helper: find the cue-specific CST node
  const find = (ruleName) =>
    cst.children?.[ruleName]?.[0] || (cst.name === ruleName ? cst : null);

  // ──────────────────────────────────────────────────
  // EXT (extension function call)
  // ──────────────────────────────────────────────────
  const extNode = find("cueExtTop");
  if (extNode) {
    return { type: "cueExt", args: extractGenericArgs(getGenericParams(extNode)) };
  }

  // ──────────────────────────────────────────────────
  // DRAW
  // ──────────────────────────────────────────────────
  const drawNode = find("cueDrawTop");
  if (drawNode) {
    return { type: "cueDraw", args: extractGenericArgs(getGenericParams(drawNode)) };
  }

  const zoomNode = find("cueZoomTop");
  if (zoomNode) {
    return { type: "cueZoom", args: extractGenericArgs(getGenericParams(zoomNode)) };
  }

  const hushNode = find("cueHushTop");
  if (hushNode) return { type: "cueHush", args: uidParamArgs(hushNode) };
  const tempoNode = find("cueTempoTop");
  if (tempoNode) {
    const args = extractGenericArgs(tempoNode.children?.genericParam || []);
    const bpm = tempoNode.children?.bpm?.[0]?.image;
    if (bpm != null && !args.some(a => a.type === "bpm")) args.unshift({ type: "bpm", value: Number(bpm) });
    return { type: "cueTempo", args };
  }

  const vizNode = find("cueVizTop");
  if (vizNode) return { type: "cueViz", args: uidParamArgs(vizNode) };

  const shapeNode = find("cueShapeTop");
  if (shapeNode) {
    return { type: "cueShape", args: extractGenericArgs(getGenericParams(shapeNode)) };
  }

  const stationNode = find("cueStationTop");
  if (stationNode) {
    return { type: "cueStation", args: extractGenericArgs(getGenericParams(stationNode)) };
  }
  const legNode = find("cueLegTop");
  if (legNode) {
    return { type: "cueLeg", args: extractGenericArgs(getGenericParams(legNode)) };
  }

  // ──────────────────────────────────────────────────
  // UI (supports trigger: for nested cues)
  // ──────────────────────────────────────────────────
  const uiNode = find("cueUiTop");
  if (uiNode) {
    const args = [];
    let triggerAst = null;
    const ch = uiNode.children;
    if (ch.selector?.[0]) {
      args.push({ type: "selector", value: unquote(ch.selector[0].image) });
    }
    // Process uiParam children
    for (const uiParam of (ch.uiParam || [])) {
      const pch = uiParam.children;
      // Check for trigger:cue
      if (pch.triggerValue?.[0]) {
        triggerAst = cstToAst(pch.triggerValue[0]);
      }
      // A regular param (a follow() in it binds, opacity: and the rest)
      if (pch.param?.[0]) {
        args.push(...extractGenericArgs([pch.param[0]]));
      }
    }
    const result = { type: "cueUi", args };
    if (triggerAst) result.triggerAst = triggerAst;
    return result;
  }

  // ──────────────────────────────────────────────────
  // FADE
  // ──────────────────────────────────────────────────
  const fadeNode = find("cueFadeTop");
  if (fadeNode) {
    const params = [];
    const list = fadeNode.children.fadeParamList?.[0];
    for (const p of (list?.children.fadeParam || [])) {
      const ch = p.children;
      const key = ch.keyMode?.[0]?.image || ch.keyIdent?.[0]?.image || "";
      let value = null;
      if (ch.call) value = maybeConvertLink(extractVal(ch.call[0]));
      else if (ch.num) value = Number(ch.num[0].image);
      else if (ch.ident) value = ch.ident[0].image;
      else if (ch.addr) value = ch.addr[0].image;
      else if (ch.str) value = ch.str[0].image.replace(/^(['"])(.*)\1$/, "$2");
      else if (ch.kwStop) value = "stop";
      params.push({ type: key, value });
    }
    return { type: "cueFade", args: params };
  }

  // ──────────────────────────────────────────────────
  // NAV
  // ──────────────────────────────────────────────────
  const navNode = find("cueNavTop");
  if (navNode) {
    const ch = navNode.children;
    const action = ch.navAction?.[0]?.image ?? null;
    const target = ch.navTarget?.[0]?.image ?? null;
    let params = {};
    if (ch.params?.[0]?.children?.genericParam) {
      for (const p of ch.params[0].children.genericParam) {
        const key = p.children.key?.[0]?.image;
        const raw = paramText(p);
        if (key && raw != null) params[key] = isStructured(raw) ? raw : unquote(raw);
      }
    }
    const uid = params.uid || (target ? `${action}@${target}` : action);
    return { type: "cueNav", action, target, uid, params };
  }

  // ──────────────────────────────────────────────────
  // PAGE
  // ──────────────────────────────────────────────────
  const pageNode = find("cuePageTop");
  if (pageNode) {
    const bodyNode = pageNode.children?.body?.[0];
    return {
      type: "cuePage",
      pattern: bodyNode?.children?.pattern?.[0]
        ? convertPatternNodeToAST(bodyNode.children.pattern[0]) : null,
      onCompletion: bodyNode?.children?.afterClause?.[0]
        ? extractAfterClause(bodyNode.children) : null,
    };
  }

  // ──────────────────────────────────────────────────
  // USE (read by preProcessReuse through parseUseDirective)
  // ──────────────────────────────────────────────────
  const useNode = find("cueUseTop");
  if (useNode) {
    const list = useNode.children.uidParamList?.[0];
    const args = extractGenericArgs(list?.children?.genericParam || []);
    const bare = list?.children?.bareUid?.[0]?.image;
    if (bare && !args.some(a => a.type === "src")) args.unshift({ type: "src", value: bare });
    return { type: "cueUse", args };
  }

  const randomNode = find("cueRandomTop");
  if (randomNode) {
    return { type: "cueRandom", args: extractGenericArgs(randomNode.children.genericParam || []) };
  }

  const adcNode = find("cueAdcTop");
  if (adcNode) {
    return { type: "cueAdc", args: extractGenericArgs(adcNode.children.genericParam || []) };
  }

  const stopwatchNode = find("cueStopwatchTop");
  if (stopwatchNode) {
    return { type: "cueStopwatch", args: extractGenericArgs(stopwatchNode.children.genericParam || []) };
  }

  // ──────────────────────────────────────────────────
  // VIDEO
  // ──────────────────────────────────────────────────
  const videoNode = find("cueVideoTop");
  if (videoNode) {
    const params = {};
    const items = videoNode.children?.videoParamList?.[0]?.children?.videoParam || [];
    for (const p of items) {
      // Keyword-token keys (audio:, pin:) land under the "key" label; plain
      // Identifier keys may be either labeled or the first Identifier child.
      const key = (p.children.key?.[0] || p.children.Identifier?.[0])?.image;
      if (!key) continue;
      // size:640x360 parses as a sizePair subrule, not a value token
      const sizeNode = p.children.sizePair?.[0];
      if (sizeNode) {
        const w = sizeNode.children.width?.[0]?.image || "0";
        const h = sizeNode.children.height?.[0]?.image || "0";
        params[key] = `${w}x${h}`;
        continue;
      }
      // list / pattern / rand() values → structured (patterns drawn per grain)
      const structured = p.children.arrayValue?.[0] || p.children.patternCall?.[0] || p.children.simpleFuncCall?.[0];
      // follow(...) → signalRef, exactly as the arg-shaped cues do above. Video
      // builds a params object rather than an args list, and was the one cue
      // this conversion never reached: a follow() stayed a bare funcCall, so it
      // bound nothing, drew no cord, and said nothing about it.
      if (structured) { params[key] = maybeConvertLink(extractVal(structured)); continue; }
      // the key is labelled key:, so an Identifier here is the value
      const valueToken = p.children.NumberLiteral?.[0] || p.children.StringLiteral?.[0] || p.children.Identifier?.[0];
      const rawVal = valueToken?.image ?? null;
      params[key] = isNaN(rawVal) ? rawVal?.replace(/^"|"$/g, "") : Number(rawVal);
    }
    return { type: "cueVideo", params };
  }

  // ──────────────────────────────────────────────────
  // TEXT, METRONOME (genericParamList → args array)
  // ──────────────────────────────────────────────────
  const textNode = find("cueTextTop");
  if (textNode) {
    return { type: "cueText", args: extractGenericArgs(getGenericParams(textNode)) };
  }

  const imageNode = find("cueImageTop");
  if (imageNode) {
    return { type: "cueImage", args: extractGenericArgs(getGenericParams(imageNode)) };
  }

  const metroNode = find("cueMetronomeTop");
  if (metroNode) {
    return { type: "cueMetronome", args: extractGenericArgs(getGenericParams(metroNode)) };
  }

  const captureNode = find("cueCaptureTop");
  if (captureNode) {
    const args = extractGenericArgs(captureNode.children?.genericParam || []);
    const word = captureNode.children?.bareWord?.[0]?.image;
    if (word) args.unshift({ type: word, value: true });
    const which = captureNode.children?.captureName?.[0]?.image;
    const types = { resources: "cueResources", screen: "cueScreen", still: "cueStill" };
    return { type: types[which] ?? "cueCapture", args };
  }

  const scatterNode = find("cueScatterTop");
  if (scatterNode) {
    const c = scatterNode.children || {};
    const args = extractGenericArgs(c.genericParam || []);
    const amount = c.amount?.[0];
    if (amount) args.unshift({ type: "amount", value: maybeConvertLink(extractVal(amount)) });
    return { type: "cueScatter", args };
  }

  const sceneNode = find("cueSceneTop");
  if (sceneNode) {
    const c = sceneNode.children || {};
    const args = extractGenericArgs(c.genericParam || []);
    // scenes(intro:16, drop:32, ...): a sequence -- each scene and its beats, in order
    if (c.sceneName?.[0]?.image === "scenes") {
      for (const w of c.bareWord || []) args.push({ type: w.image, value: true });
      return { type: "cueScenes", args };
    }
    // console(s1, perform, cut): its words said as flags, the scene's name the one word that is none
    if (c.sceneName?.[0]?.image === "console") {
      const FLAGS = { perform: "perform", panel: "panel", open: "open", close: "close", off: "close", cut: "cut", clear: "clear", add: "add" };
      for (const w of c.bareWord || []) {
        if (FLAGS[w.image]) args.push({ type: FLAGS[w.image], value: true });
        else if (!args.some(a => a.type === "name")) args.unshift({ type: "name", value: w.image });
      }
      return { type: "cueConsole", args };
    }
    // scene(drop, cut): the scene's name, and a cut (no fade) said in a word
    for (const w of c.bareWord || []) {
      if (w.image === "cut") args.push({ type: "cut", value: true });
      // scene(s1b, add): on top of the scene that is on (liveScenes.js addLayer)
      else if (w.image === "add") args.push({ type: "add", value: true });
      else if (!args.some(a => a.type === "name")) args.unshift({ type: "name", value: w.image });
    }
    return { type: "cueScene", args };
  }

  const camNode = find("cueCamTop");
  if (camNode) {
    const c = camNode.children || {};
    const args = extractGenericArgs(c.genericParam || []);
    // cam(off): every camera off; cam(me, ...): the uid, as viz(s3, ...) names its box
    for (const w of c.bareWord || []) {
      if (w.image === "off") args.push({ type: "off", value: true });
      else if (!args.some(a => a.type === "uid")) args.unshift({ type: "uid", value: w.image });
    }
    return { type: "cueCam", args };
  }

  const strobeNode = find("cueStrobeTop");
  if (strobeNode) {
    const c = strobeNode.children || {};
    const args = extractGenericArgs(c.genericParam || []);
    const amount = c.amount?.[0];
    if (amount) args.unshift({ type: "amount", value: maybeConvertLink(extractVal(amount)) });
    if (c.bareWord?.length) args.push({ type: "off", value: true });
    return { type: "cueStrobe", args };
  }

  const gridNode = find("cueGridTop");
  if (gridNode) {
    const c = gridNode.children || {};
    const args = extractGenericArgs(c.genericParam || []);
    // grid(3, 2): columns, rows; grid(4): 4 by 4
    const counts = (c.count || []).map(n => maybeConvertLink(extractVal(n)));
    if (counts.length) {
      if (!args.some(a => a.type === "cols")) args.push({ type: "cols", value: counts[0] });
      if (!args.some(a => a.type === "rows")) args.push({ type: "rows", value: counts[1] ?? counts[0] });
    }
    for (const w of c.bareWord || []) args.push({ type: w.image, value: true });
    return { type: "cueGrid", args };
  }

  const sayNode = find("cueSayTop");
  if (sayNode) {
    const c = sayNode.children || {};
    const args = extractGenericArgs(c.genericParam || []);
    // a bare name is its uid, a bare string what it says
    if (c.bareWord?.length && !args.some(a => a.type === "uid")) args.unshift({ type: "uid", value: c.bareWord[0].image });
    const named = c.sayName?.[0]?.image;
    // style(agent, "font-size:32px; ...", zoom:12): the string is the css
    if (named === "style") {
      for (const w of (c.bareWord || []).slice(1)) args.push({ type: w.image, value: true });   // style(agent, off)
      if (c.bareText?.length) args.push({ type: "css", value: c.bareText.map(t => unquote(t.image)).join("; ") });
      return { type: "cueStyle", args };
    }
    if (c.bareText?.length) args.push({ type: "text", value: c.bareText.map(t => unquote(t.image)).join(" ") });
    return { type: "cueSay", args };
  }

  const levelNode = find("cueLevelTop");
  if (levelNode) {
    const c = levelNode.children || {};
    const args = extractGenericArgs(c.genericParam || []);
    const names = [];
    for (const l of c.bareList || []) names.push(...[].concat(extractVal(l)).map(String));
    for (const w of c.bareWord || []) names.push(w.image);
    if (names.length) args.push({ type: "names", value: names });
    // a relative value in place of a number: level(agents, *0.5) -- in order among them
    const nums = [...(c.bareNum || []), ...(c.bareRel || [])].sort((a, b) => a.startOffset - b.startOffset)
      .map(t => (t.tokenType.name === "RelLiteral" ? relLiteral(t.image) : Number(t.image)));
    if (nums.length) args.push({ type: "numbers", value: nums });
    for (const v of c.bareValue || []) args.push({ type: "value", value: maybeConvertLink(extractVal(v)) });
    const named = c.levelName[0].image;
    // fx(room, [a1, a2], reverb:{...}) / group(agents, [a1, a2]) /
    // palette(mine, [red, #05d9e8]): the first bare word is the bus, the
    // group or the palette; the rest its sounds, or its colours
    if (named === "fx" || named === "group" || named === "palette") {
      const words = (c.bareWord || []).map(w => w.image);
      const rest = [...words.slice(1)];
      for (const l of c.bareList || []) rest.push(...[].concat(extractVal(l)).map(String));
      const out = args.filter(a => a.type !== "names");
      if (words.length && !out.some(a => a.type === "uid")) out.unshift({ type: "uid", value: words[0] });
      if (rest.length) out.push({ type: "names", value: rest });
      return { type: named === "fx" ? "cueFx" : named === "palette" ? "cuePalette" : "cueGroup", args: out };
    }
    // wait(16): in a block run in turn (a scene, liveScenes.js), what follows waits so many beats
    const types = { xfade: "cueXfade", hold: "cueHold", gate: "cueGate", seq: "cueSeq", bang: "cueBang", solo: "cueSolo", wait: "cueWait", speakers: "cueSpeakers" };
    return { type: types[named] ?? "cueLevel", args };
  }

  const vuNode = find("cueVuTop");
  if (vuNode) {
    const args = extractGenericArgs(vuNode.children?.genericParam || []);
    for (const w of vuNode.children?.bareWord || []) {
      if (w.image === "stop" || w.image === "off") args.push({ type: "stop", value: true });
      else args.push({ type: "name", value: w.image });
    }
    return { type: "cueVu", args };
  }

  const urlNode = find("cueUrlTop");
  if (urlNode) {
    return { type: "cueUrl", args: extractGenericArgs(getGenericParams(urlNode)) };
  }

  const sinkNode = find("cueSinkTop");
  if (sinkNode) {
    const name = sinkNode.children.sinkName?.[0]?.image;
    return { type: name === "oscOut" ? "cueOscOut" : "cueDisplay", args: extractGenericArgs(getGenericParams(sinkNode)) };
  }

  // ──────────────────────────────────────────────────
  // OSC (with trailing param list + special func calls)
  // ──────────────────────────────────────────────────
  const oscNode = find("cueOscTop");
  if (oscNode) {
    const items = [];
    const mainList = oscNode.children.genericParamList?.[0];
    if (mainList?.children?.genericParam) items.push(...mainList.children.genericParam);
    const trailingList = oscNode.children.trailingParamList?.[0];
    if (trailingList?.children?.genericParam) items.push(...trailingList.children.genericParam);

    const args = [];
    for (const p of items) {
      const key = p.children.key?.[0]?.image;
      if (!key) continue;
      let val = null;
      const v = p.children.value?.[0];
      if (v?.name === "simpleFuncCall") val = extractOscFuncCall(v);
      if (val === null && v?.image != null) {
        val = parseRaw(String(v.image).replace(/^"|"$/g, ""));
      }
      // anything else (a list: args:[…]) the way every other cue reads it
      if (val === null && v) val = extractVal(v);
      args.push({ type: key, value: val });
    }
    return { type: "cueOsc", args };
  }

  // ──────────────────────────────────────────────────
  // MIDI
  // ──────────────────────────────────────────────────
  const midiNode = find("cueMidiTop");
  if (midiNode) {
    return { type: "cueMidi", args: extractGenericArgs(getGenericParams(midiNode)) };
  }

  // ──────────────────────────────────────────────────
  // OSC_CTRL
  // ──────────────────────────────────────────────────
  const oscCtrlNode = find("cueOscCtrlTop");
  if (oscCtrlNode) {
    return { type: "oscCtrl", args: extractGenericArgs(getGenericParams(oscCtrlNode)) };
  }

  // ──────────────────────────────────────────────────
  // CONTROLXY
  // ──────────────────────────────────────────────────
  const controlXYNode = find("cueControlXYTop");
  if (controlXYNode) {
    return { type: "cueControlXY", args: extractGenericArgs(getGenericParams(controlXYNode)) };
  }

  // ──────────────────────────────────────────────────
  // SPEED
  // ──────────────────────────────────────────────────
  const speedNode = find("cueSpeedTop");
  if (speedNode) {
    const fnName = speedNode.children?.speedFn?.[0]?.image;
    if (fnName !== "speed") return null;
    const shorthandTok = speedNode.children?.shorthand?.[0];
    let value = shorthandTok ? Number(shorthandTok.image) : null;
    let add = null, dur = null, ease = null, uid = null, loop = null, revtrig = null;
    for (const p of (speedNode.children.genericParam || [])) {
      const key = p.children.key?.[0]?.image;
      const raw = paramText(p);
      if (!key) continue;
      const v = rawOr(raw);
      if (key === "value" || key === "speed" || key === "multiplier") value = numOr(v);
      else if (key === "add") add = Number(v);
      // dur:edge — the ramp spans the element's own drawn width instead of a
      // time: it completes exactly when the playhead leaves the bounding box,
      // so a rall drawn as a group with an extent handle is visually exact.
      // (bb / extent are accepted as synonyms.)
      else if (key === "dur") dur = /^(edge|bb|extent)$/i.test(String(v)) ? "edge" : numOr(v);
      else if (key === "ease" || key === "easing") ease = String(v);
      else if (key === "uid") uid = v;
      else if (key === "loop") loop = Number(v);
      else if (key === "revtrig") revtrig = Number(v);
    }
    return { type: "cueSpeed", value, add, dur, ease, uid, loop, revtrig };
  }

  // ──────────────────────────────────────────────────
  // BUTTON
  // ──────────────────────────────────────────────────
  const buttonNode = find("cueButtonTop");
  if (buttonNode) {
    const labelTok = buttonNode.children.labelValue?.[0];
    const label = labelTok ? labelTok.image.replace(/^"|"$/g, "") : "";
    const triggerAst = buttonNode.children.triggerValue?.[0]
      ? cstToAst(buttonNode.children.triggerValue[0]) : null;
    const opt = {};
    // Extract direct button params (e.g., mode:overlay)
    for (const p of (buttonNode.children.buttonParam || [])) {
      const keyNode = p.children.key?.[0];
      const valNode = p.children.value?.[0];
      if (keyNode && valNode) {
        const val = extractVal(valNode);
        opt[keyNode.image] = val;
      }
    }
    // Extract style(...) params into opt.style
    const styleParams = buttonNode.children.styleParam || [];
    if (styleParams.length > 0) {
      opt.style = {};
      for (const p of styleParams) {
        const keyNode = p.children.key?.[0];
        const valNode = p.children.value?.[0];
        if (keyNode && valNode) {
          const val = extractVal(valNode);
          opt.style[keyNode.image] = val;
        }
      }
    }
    return {
      type: "cueButton", label,
      triggerAst: { ...triggerAst, uid: triggerAst?.uid ?? null },
      opt
    };
  }

  // ──────────────────────────────────────────────────
  // AUDIO / AUDIO_POOL / AUDIO_IMPULSE (shared extraction)
  // ──────────────────────────────────────────────────
  const audioNode = find("cueAudioTop");
  if (audioNode) {
    const params = extractGenericParamsFlat(getGenericParams(audioNode), { bindings: true });
    const { src, amp = 1, loop = 1, toggle = false, fade, fadeIn = fade, fadeOut = fade, uid = src } = params;
    return { type: "cueAudio", src, amp, loop, fadeIn: fadeIn ?? 0, fadeOut: fadeOut ?? 0, toggle, uid, params };
  }

  const audioPoolNode = find("cueAudioPoolTop");
  if (audioPoolNode) {
    const params = extractGenericParamsFlat(getGenericParams(audioPoolNode), { bindings: true });
    const { path, glob, format = "wav", mode = "shuffle", uid, group } = params;
    return { type: "cueAudioPool", path, glob, format, mode, uid, group, params };
  }

  const audioImpulseNode = find("cueAudioImpulseTop");
  if (audioImpulseNode) {
    const params = extractGenericParamsFlat(getGenericParams(audioImpulseNode), { bindings: true });
    const { path, glob, format = "wav", mode = "shuffle", rate = 30, jitter = 0, lifetime = "region", uid, group } = params;
    return { type: "cueAudioImpulse", path, glob, format, mode, rate, jitter, lifetime, uid, group, params };
  }

  // ──────────────────────────────────────────────────
  // SYNTH
  // ──────────────────────────────────────────────────
  const synthNode = find("cueSynthTop");
  if (synthNode) {
    return { type: "cueSynth", args: extractGenericArgs(getGenericParams(synthNode)) };
  }

  // ──────────────────────────────────────────────────
  // NOTATION (static visual — rendered at load)
  // ──────────────────────────────────────────────────
  const notationNode = find("cueNotationTop");
  if (notationNode) {
    const params = extractGenericParamsFlat(getGenericParams(notationNode));
    return { type: "cueNotation", params };
  }

  // ──────────────────────────────────────────────────
  // PAUSE
  // ──────────────────────────────────────────────────
  const pauseNode = find("cuePauseTop");
  if (pauseNode) {
    // pause(4) is pause(dur:4). count stays unset unless given: pause.js shows
    // the countdown unless count is false.
    const p = extractGenericParamsFlat(pauseNode.children.genericParam || []);
    const shorthand = pauseNode.children.shorthandDur?.[0]?.image;
    const dur = shorthand != null ? Number(shorthand) : (p.dur != null ? Number(p.dur) : null);
    const pause = { type: "cuePause", dur, next: p.next != null ? String(p.next) : null };
    if (p.count != null) pause.count = (p.count === true || p.count === "true" || p.count === 1 || p.count === "1");
    return pause;
  }

  // ──────────────────────────────────────────────────
  // PIN (standalone)
  // ──────────────────────────────────────────────────
  const pinNode = find("cuePinTop");
  if (pinNode) {
    // Shorthand: pin(30) → dur=30; 0 / absent = held until released
    const shorthandTok = pinNode.children?.shorthandDur?.[0];
    let dur = shorthandTok ? Number(shorthandTok.image) : null;
    const out = { type: "cuePin", dur };
    // pin(now): engage:now -- held where it is from this moment (cuePin.js)
    // pin(wrap): engage:wrap -- scrolling with the score, round the screen
    const pinWord = pinNode.children?.pinWord?.[0]?.image;
    if (pinWord === "now" || pinWord === "wrap") out.engage = pinWord;
    const unquote = (v) => (v == null ? v : String(v).replace(/^(['"])(.*)\1$/, "$2"));
    // Named params: dur, edge (playhead|start|end|left|right), glide (s),
    // engage (playhead|trailing|edge — hold when the centre, or the trailing
    // edge, reaches the playhead; or scroll on undisturbed and hold once the
    // element reaches the screen edge), uid (name for release), release (uid
    // of the pin this element lets go), until (id of the element whose
    // arrival at the playhead releases THIS pin — the one-object form)
    for (const p of (pinNode.children.genericParam || [])) {
      const key = p.children.key?.[0]?.image;
      const raw = paramText(p);
      if (key === "dur") out.dur = numOr(raw);
      else if (key === "glide") out.glide = numOr(raw);
      // fade: beats -- let go, it fades out over them where it is held, and goes
      else if (key === "fade") out.fade = numOr(raw);
      else if (key === "margin") out.margin = numOr(raw);
      // live:1 — cues inside fire where the pin HOLDS them (at the playhead),
      // not where they were drawn: a pinned rotating sky whose stars must meet
      // the playhead line. See cueDispatcher.js (pin shift).
      else if (key === "live") out.live = (raw === "1" || raw === "true");
      // viewport:<anchor> — held against the SCREEN rather than the playhead:
      // it ignores zoom, pan and the playhead entirely. size/margin/opacity
      // place it there. See cuePin.js.
      else if (key === "viewport" || key === "size" || key === "opacity"
            || key === "edge" || key === "uid" || key === "release"
            || key === "engage" || key === "until" || key === "then") out[key] = isStructured(raw) ? raw : unquote(raw);
    }
    return out;
  }

  // ──────────────────────────────────────────────────
  // PLAY / REWIND / SEEK
  // ──────────────────────────────────────────────────
  const transportNode = find("cueTransportTop");
  if (transportNode) {
    const fn = transportNode.children.transportFn?.[0]?.image;
    const amount = transportNode.children.amount?.[0]?.image;
    if (fn === "seek") {
      if (amount == null) return null;        // seek() -- to where?
      return {
        type: "cueSeek",
        value: Number(amount),
        relative: /^[+-]/.test(amount),
        unit: transportNode.children.percent ? "%" : "s",
      };
    }
    if (amount != null) return null;          // play(3), rewind(3): not a thing
    return { type: fn === "play" ? "cuePlay" : "cueRewind" };
  }

  // ──────────────────────────────────────────────────
  // STOP
  // ──────────────────────────────────────────────────
  const stopNode = find("cueStopTop");
  if (stopNode) {
    const params = {};
    for (const p of (stopNode.children.genericParam || [])) {
      const key = p.children.key?.[0]?.image;
      if (key) params[key] = rawOr(paramText(p));
    }
    return { type: "cueStop", ...params };
  }

  // ──────────────────────────────────────────────────
  // ANIMATION: ROTATE, SCALE, O2P, COLOR
  // ──────────────────────────────────────────────────
  const rotNode = find("cueRotateTop");
  if (rotNode) return { type: "cueRotate", args: extractAnimKvArgs(rotNode) };

  const transNode = find("cueTransTop");
  if (transNode) return { type: "cueTrans", args: extractAnimKvArgs(transNode) };

  const scNode = find("cueScaleTop");
  if (scNode) return { type: "cueScale", args: extractAnimKvArgs(scNode) };

  const o2pNode = find("cueO2PTop");
  if (o2pNode) return { type: "cueO2P", args: extractAnimKvArgs(o2pNode) };

  const colorNode = find("cueColorTop");
  if (colorNode) return { type: "cueColor", args: extractAnimKvArgs(colorNode) };

  // ──────────────────────────────────────────────────
  // FALLBACK
  // ──────────────────────────────────────────────────
  console.warn("[CueDSL] Unrecognized CST structure:", cst.name);
  return { type: "cueUnknown", args: [] };
}

// ============================================================================
// 5️⃣ MAIN ENTRY
// ============================================================================
// ONE parser for the whole session. Chevrotain builds a parser's lookahead
// tables and validates the grammar in the constructor (performSelfAnalysis),
// which costs ~8 ms — per parse, that dominates loading a large score. A
// CstParser is made to be reused: assigning .input resets it.
let _parser = null;
function sharedParser() {
  if (!_parser) _parser = new CueParser();
  return _parser;
}

/**
 * every(4, beat.hit) -> beat.hit4, beat.div(4) -> beat.div4: a count is an
 * argument, as numbers are everywhere else in the language (tile:3,
 * Pseq(.., inf)), not part of a signal's name. The bus keeps the channel
 * names (liveSignals.js makes hit4 and div4 while something follows them),
 * so the old spelling still works. every(3, beat.div(4)) -> beat.div4e3:
 * every 3rd 16th (dotted 8ths), counted on the shared grid. every() counts
 * hits and the clock's parts only: every(4, beat.phase) is said to be
 * wrong, and left to fail.
 */
export function countsToChannels(text) {
  const out = String(text)
    .replace(/\bevery\s*\(\s*(\d+)\s*,\s*([A-Za-z_][\w-]*)\.div\s*(?:\(\s*(\d+)\s*\)|(\d+))\s*\)/g,
      (m, k, src, a, b) => `${src}.div${a ?? b}e${k}`)
    .replace(/\bevery\s*\(\s*(\d+)\s*,\s*([A-Za-z_][\w-]*)\.hit\s*\)/g, "$2.hit$1")
    .replace(/\b([A-Za-z_][\w-]*)\.div\s*\(\s*(\d+)\s*\)/g, "$1.div$2")
    // bass.band(2, 4): the 2nd of 4 bands of its spectrum (spectrumBands.js)
    .replace(/\b([A-Za-z_][\w-]*)\.band\s*\(\s*(\d+)\s*,\s*(\d+)\s*\)/g, "$1.band$2of$3");
  if (/\bevery\s*\(/.test(out)) console.warn("[every] counts hits and the beat's parts: every(4, beat.hit), every(8, bass.hit), every(3, beat.div(4))");
  return out;
}

// ---- arithmetic on values: freq:[110, 220.7, 330.4]/2 -----------------
//
//   freq:[110, 220.7, 330.4]/2      each of them: [55, 110.35, 165.2]
//   degree:Pseq(0, 3, 5, inf)+2     every step of the pattern, as it comes
//   freq:Pwhite(30, 300)*4          the range: Pwhite(120, 1200)
//   amp:rand(0.1, 0.2)*0.5          its bounds; dur:1/3 a third of a beat
//
// An operator (* / + -) and a number after a list (]), a call or pattern
// ()) or a number is worked out on what it follows, left to right. Written
// first, a value with an operator is relative to what is playing now
// (freq:*1.5, relative.js), and stays so; [-1, 2] and a name like s3-4 are
// not sums. The sum is made a call here, opv(x, "/", 2), so the grammar
// carries it, and worked out on the parsed line (applyArithmetic).

const NUM_AT = /^\s*(-?(?:\d+\.?\d*|\.\d+))(?![\w.~#/:])/;

/** The first `x op n` in `text` (outside quotes): where x starts, the op, the number. */
function nextSum(text) {
  let quote = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quote) { if (ch === quote) quote = ""; continue; }
    if (ch === '"' || ch === "'") { quote = ch; continue; }
    if (!"*/+-".includes(ch) || text[i + 1] === "=") continue;
    const m = NUM_AT.exec(text.slice(i + 1));
    if (!m) continue;
    let j = i - 1;
    while (j >= 0 && text[j] === " ") j--;
    const c = text[j];
    let start = -1;
    if (c === "]" || c === ")") {
      const open = c === "]" ? "[" : "(";
      let depth = 0, k = j;
      for (; k >= 0; k--) { if (text[k] === c) depth++; else if (text[k] === open && --depth === 0) break; }
      if (k < 0) continue;
      if (c === ")") while (k > 0 && /[\w.]/.test(text[k - 1])) k--;     // the call's name: rand(...), Pseq(...)
      start = k;
    } else if (/[\d.]/.test(c ?? "")) {
      let k = j;
      while (k > 0 && /[\d.]/.test(text[k - 1])) k--;
      if (k > 0 && /[\w~#./]/.test(text[k - 1])) continue;       // s3-4, ~A1, a hex colour, /osc/1/2: not a number
      if (k > 0 && text[k - 1] === "-" && !(k > 1 && /[\w\])]/.test(text[k - 2]))) k--;   // a negative one: -2*3
      start = k;
    } else continue;
    // a value written with its operator first (freq:*1.5) is relative: no left side
    return { start, end: j + 1, op: ch, num: m[1], after: i + 1 + m[0].length };
  }
  return null;
}

/** Each `x op n` in the text made a call the grammar reads: opv(x, "op", n). */
export function arithmeticToCalls(text) {
  let out = String(text);
  if (!/[\])\d.]\s*[*/+-]\s*-?\.?\d/.test(out)) return out;
  for (let guard = 0; guard < 64; guard++) {
    const at = nextSum(out);
    if (!at) break;
    out = `${out.slice(0, at.start)}opv(${out.slice(at.start, at.end)}, "${at.op}", ${at.num})${out.slice(at.after)}`;
  }
  return out;
}

export const RANDOM_CALLS = new Set(["rand", "irand", "birand", "hrand", "rnd", "irnd", "choose"]);
const RANGE_PATTERNS = new Set(["Pwhite", "Pexprand"]);
// patterns whose arguments are not values to draw (weights, a mean and a
// spread, k hits in n): an op works on each value drawn, not on them (patterns.js ops)
// A pattern's named options, written key:value among its values: peuclid(3, 8, rot:2)
const PATTERN_OPTIONS = { Peuclid: ["rot"] };
const DRAWN_PATTERNS = new Set(["Pwrand", "Pbrown", "Pgauss", "Plprand", "Phprand", "Pmeanrand", "Pbeta", "Pcauchy", "Ppoisson", "Pbjorklund", "Peuclid"]);

/** `v` op `k`, through lists, patterns and random calls; what cannot be worked out, as it was. */
export function applyOp(v, op, k) {
  const f = x => (op === "*" ? x * k : op === "/" ? x / k : op === "+" ? x + k : x - k);
  if (typeof v === "number") return f(v);
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return f(Number(v));
  if (Array.isArray(v)) return v.map(x => applyOp(x, op, k));
  if (v && typeof v === "object") {
    if (v.type === "pattern" && DRAWN_PATTERNS.has(v.name)) {
      const rel = op === "/" ? { op: "*", by: 1 / k } : op === "-" ? { op: "+", by: -k } : { op, by: k };
      return { ...v, ops: [...(v.ops || []), { type: "rel", ...rel }] };
    }
    if (v.type === "pattern") {
      const out = { ...v, values: (v.values || []).map(x => applyOp(x, op, k)) };
      // Pwhite(30, 300): the parser keeps the top of the range as its repeats
      if (RANGE_PATTERNS.has(v.name) && (v.values || []).length === 1 && Number.isFinite(Number(v.repeats))) out.repeats = f(Number(v.repeats));
      return out;
    }
    if (v.type === "funcCall" && v.name === "opv") return applyOp(applyOp(v.args?.[0], v.args?.[1], Number(v.args?.[2])), op, k);
    if (v.type === "funcCall" && RANDOM_CALLS.has(v.name)) return { ...v, args: (v.args || []).map(x => applyOp(x, op, k)) };
  }
  console.warn(`[arithmetic] ${op}${k} on ${JSON.stringify(v)?.slice(0, 40)}: only numbers, lists, patterns and rand() / choose() can be worked out`);
  return v;
}

/** Every opv(...) in a parsed line worked out (arithmeticToCalls made them). */
function applyArithmetic(v) {
  if (Array.isArray(v)) return v.map(applyArithmetic);
  if (!v || typeof v !== "object") return v;
  if (v.type === "funcCall" && v.name === "opv") return applyOp(applyArithmetic(v.args?.[0]), v.args?.[1], Number(v.args?.[2]));
  const out = {};
  for (const key in v) out[key] = applyArithmetic(v[key]);
  return out;
}

/**
 * quiet: no console.error on a parse error -- for a caller that says what
 * went wrong itself (the live console checks every line as it is typed)
 */
export function parseCueToAST(input, { quiet = false } = {}) {
  // use() is a preprocessor directive, not a cue: it is expanded by
  // preProcessReuse before assignCues ever sees the element, and must never
  // reach the dispatcher. Kept returning null here so every existing caller
  // behaves exactly as before; the preprocessor reads its parameters through
  // parseUseDirective() below.
  if (input.trim().startsWith("use(")) return null;

  // Strip Inkscape-added suffixes like -2, -7, -15 from end of cue expressions
  // e.g. "ui(uid:foo, action:toggle, click:1)-2" → "ui(uid:foo, action:toggle, click:1)"
  input = input.replace(/\)-\d+$/, ')');
  input = countsToChannels(input);
  input = arithmeticToCalls(input);

  const lexResult = CueLexer.tokenize(input);

  if (OSCILLA_DSL_DEBUG) {
    console.log("[LexerDebug] Input:", input);
    console.log("[LexerDebug] Tokens:", lexResult.tokens.map(t => `${t.tokenType.name}:"${t.image}"`));
    console.log("[LexerDebug] Errors:", lexResult.errors);
  }

  const parser = sharedParser();
  parser.input = lexResult.tokens;
  const cst = parser.cueTop();

  if (parser.errors.length) {
    if (!quiet) console.error("[CueDSL] ❌ Parse errors:", parser.errors);
    // The details travel with the error for callers that show them to a
    // person (the live console); everyone else only needs that it threw.
    const err = new Error("Parsing failed");
    err.parseErrors = parser.errors;
    err.lexErrors = lexResult.errors;
    throw err;
  }
  // A character the lexer skipped (rotate(dur:2)!) leaves a cue that parses
  if (lexResult.errors.length && !quiet) {
    console.warn(`[CueDSL] skipped characters in ${input}:`, lexResult.errors.map(e => e.message).join("; "));
  }

  if (OSCILLA_DSL_DEBUG) {
    console.log("✅ Parsed CST structure:");
    printCST(cst);
  }

  let ast = cstToAst(cst);
  if (ast && /\bopv\(/.test(input)) ast = applyArithmetic(ast);
  // colour: is color: -- every cue that takes one reads color:
  for (const a of ast?.args || []) if (a?.type === "colour") a.type = "color";

  if (OSCILLA_DSL_DEBUG) {
    console.log("[CueDSL] ✅ Final AST:", JSON.stringify(ast, null, 2));
  }

  return ast;
}

/**
 * Parse a use(...) directive into { type:"cueUse", args }.
 *
 * A regex cannot cope with nested calls — use(path:blocks,
 * index:Prand(0,1,2)) has parens inside the parens — so this uses the same
 * lexer and grammar every other cue gets.
 *
 * Any @placement suffix must be stripped by the caller; it is not DSL.
 *
 * @param {string} input
 * @returns {{type:string,args:Array}|null} null if it is not a parseable use()
 */
export function parseUseDirective(input) {
  const src = String(input ?? "").trim().replace(/\)-\d+$/, ")");
  if (!/^use\s*\(/.test(src)) return null;
  try {
    const lexResult = CueLexer.tokenize(src);
    if (lexResult.errors?.length) return null;
    const parser = sharedParser();
    parser.input = lexResult.tokens;
    const cst = parser.cueTop();
    if (parser.errors.length) return null;
    const ast = cstToAst(cst);
    return ast?.type === "cueUse" ? ast : null;
  } catch {
    return null;
  }
}

window.parseCueToAST = parseCueToAST;
